import React, { useRef, useEffect, useState, useCallback, useImperativeHandle, forwardRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Dropdown, Modal, Form, Input, Tooltip } from 'antd';
import {
    SendOutlined,
    BoldOutlined,
    ItalicOutlined,
    UnderlineOutlined,
    StrikethroughOutlined,
    LinkOutlined,
    UnorderedListOutlined,
    OrderedListOutlined,
    CommentOutlined,
    PlusOutlined,
    CameraOutlined,
    PictureOutlined,
    CloudUploadOutlined,
    CustomerServiceOutlined,
    VideoCameraOutlined,
    AudioOutlined,
    SmileOutlined,
    FileImageOutlined,
    FileOutlined,
    CloseOutlined,
    PaperClipOutlined,
    LeftOutlined,
    RightOutlined,
} from '@ant-design/icons';
import styled, { useTheme, keyframes } from 'styled-components';
import { Container } from '@/components/ui/Container';
import AntImagePreview from '@/components/ui/AntImagePreview';
import { Button } from '@/components/ui/Button';
import AppModal from '@/pages/inside/tenant/AppModal';
import { CreateRoleModalBody, CreateRoleModalFooter } from '@/pages/inside/tenant/style';
import { htmlToMarkdown } from '@utils/htmlToMarkdown.util';
import { storedChatBodyToComposeHtml } from '@utils/markdownToComposeHtml.util';
import { normalizePlainUrlCandidateToHref, trimTrailingPunctuation } from '@utils/chatPlainTextLinkify.util';
import { getFileIconSrc } from '@utils/fileIcon.util';

const composeRecordingWavePulse = keyframes`
    0%,
    100% {
        transform: scaleY(0.38);
        opacity: 0.55;
    }
    50% {
        transform: scaleY(1);
        opacity: 1;
    }
`;

const RecordingWaveBarsRoot = styled.span`
    display: inline-flex;
    align-items: flex-end;
    gap: 3px;
    height: 26px;
    padding: 0 4px 0 0;
`;

const RecordingWaveBar = styled.span`
    width: 3px;
    min-height: 4px;
    border-radius: 2px;
    background: ${({ $color }) => $color};
    transform-origin: bottom center;
    height: ${({ $h }) => $h}px;
    animation: ${composeRecordingWavePulse} 0.75s ease-in-out infinite;
    animation-delay: ${({ $delay }) => $delay}s;
`;

function RecordingWaveBars({ color }) {
    const heights = [8, 13, 18, 11];
    return (
        <RecordingWaveBarsRoot aria-hidden>
            {heights.map((h, i) => (
                <RecordingWaveBar key={i} $h={h} $delay={i * 0.12} $color={color} />
            ))}
        </RecordingWaveBarsRoot>
    );
}

/** After programmatic load, caret at end (edit mode) instead of start of contentEditable. */
function moveCaretToEndOfElement(el) {
    if (!el || typeof document === 'undefined') return;
    el.focus();
    const sel = window.getSelection();
    if (!sel) return;
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    sel.removeAllRanges();
    sel.addRange(range);
}

/**
 * Reliable blockquote detection for contentEditable (use Selection + Range nodes, not Range.anchorNode — it does not exist).
 */
function getBlockquoteFromSelection(editorRoot, selection) {
    if (!editorRoot || !selection?.rangeCount) return null;
    const range = selection.getRangeAt(0);
    const nodes = [
        selection.anchorNode,
        selection.focusNode,
        range.startContainer,
        range.endContainer,
        range.commonAncestorContainer,
    ];
    for (const n of nodes) {
        if (!n || !editorRoot.contains(n)) continue;
        const el = n.nodeType === Node.TEXT_NODE ? n.parentElement : n;
        const bq = el?.closest?.('blockquote');
        if (bq && editorRoot.contains(bq)) return bq;
    }
    return null;
}

/** Nearest <li> for selection (same node walk as blockquote). */
function getListItemFromSelection(editorRoot, selection) {
    if (!editorRoot || !selection?.rangeCount) return null;
    const range = selection.getRangeAt(0);
    const nodes = [
        selection.anchorNode,
        selection.focusNode,
        range.startContainer,
        range.endContainer,
        range.commonAncestorContainer,
    ];
    for (const n of nodes) {
        if (!n || !editorRoot.contains(n)) continue;
        const el = n.nodeType === Node.TEXT_NODE ? n.parentElement : n;
        const li = el?.closest?.('li');
        if (li && editorRoot.contains(li)) return li;
    }
    return null;
}

/**
 * New list item (next bullet/number). insertParagraph splits <li> in Chromium like Enter in a list.
 */
function insertNewListItemAtCaret() {
    if (typeof document === 'undefined') return false;
    try {
        return document.execCommand('insertParagraph');
    } catch {
        return false;
    }
}

/**
 * Insert a soft line break without execCommand — insertLineBreak can split <li>/<blockquote> and hide markers in Chromium.
 * Adds a zero-width space after <br> so the caret stays stable; stripped on markdown export.
 */
function insertSoftBreakAtCaret(editorRoot) {
    if (typeof document === 'undefined' || !editorRoot) return false;
    const sel = document.getSelection();
    if (!sel?.rangeCount) return false;
    const range = sel.getRangeAt(0);
    if (!editorRoot.contains(range.commonAncestorContainer)) return false;

    range.deleteContents();
    const br = document.createElement('br');
    range.insertNode(br);
    const pad = document.createTextNode('\u200b');
    br.parentNode?.insertBefore(pad, br.nextSibling);
    const nextRange = document.createRange();
    nextRange.setStart(pad, 1);
    nextRange.collapse(true);
    sel.removeAllRanges();
    sel.addRange(nextRange);
    return true;
}

function closestTagFromSelection(editorRoot, tagName) {
    if (!editorRoot || typeof document === 'undefined') return null;
    const sel = document.getSelection();
    if (!sel?.rangeCount) return null;
    const range = sel.getRangeAt(0);
    const nodes = [
        sel.anchorNode,
        sel.focusNode,
        range.startContainer,
        range.endContainer,
        range.commonAncestorContainer,
    ];
    for (const n of nodes) {
        if (!n || !editorRoot.contains(n)) continue;
        const el = n.nodeType === Node.TEXT_NODE ? n.parentElement : n;
        const hit = el?.closest?.(tagName);
        if (hit && editorRoot.contains(hit)) return hit;
    }
    return null;
}

function unwrapBlockElement(el) {
    if (!el || !el.parentNode) return;
    const doc = el.ownerDocument || document;
    const frag = doc.createDocumentFragment();
    while (el.firstChild) frag.appendChild(el.firstChild);
    // Ensure caret doesn't get stuck; add a newline after unwrapping.
    frag.appendChild(doc.createElement('br'));
    el.parentNode.replaceChild(frag, el);
}

/** Move caret to a new line after a block (pre/blockquote) without deleting the block. */
function exitBlockElementToBodyAfter(blockEl) {
    if (!blockEl?.parentNode || typeof document === 'undefined') return false;
    const doc = blockEl.ownerDocument || document;
    const parent = blockEl.parentNode;
    const sel = doc.getSelection();
    if (!sel) return false;
    const br = doc.createElement('br');
    const pad = doc.createTextNode('\u200b');
    if (blockEl.nextSibling) {
        parent.insertBefore(br, blockEl.nextSibling);
        parent.insertBefore(pad, br.nextSibling);
    } else {
        parent.appendChild(br);
        parent.appendChild(pad);
    }
    const r = doc.createRange();
    r.setStart(pad, 1);
    r.collapse(true);
    sel.removeAllRanges();
    sel.addRange(r);
    return true;
}

/**
 * Shift+Enter while in inline code: new line + new <code> chip (Slack-like), splitting tail if caret mid-line.
 */
function shiftEnterNewInlineCodeInstance(codeEl, editorRoot) {
    if (!codeEl?.parentNode || typeof document === 'undefined') return false;
    const doc = codeEl.ownerDocument || document;
    const sel = doc.getSelection();
    if (!sel?.rangeCount) return false;
    const range = sel.getRangeAt(0);
    if (!codeEl.contains(range.commonAncestorContainer) && range.commonAncestorContainer !== codeEl) return false;
    if (!range.collapsed) range.deleteContents();

    const parent = codeEl.parentNode;
    const br = doc.createElement('br');
    const newCode = doc.createElement('code');
    newCode.appendChild(doc.createTextNode('\u200b'));

    const atEnd = isCollapsedCaretAtEndOfInlineCode(editorRoot, sel);
    if (!atEnd) {
        const tail = range.cloneRange();
        tail.selectNodeContents(codeEl);
        tail.setStart(range.startContainer, range.startOffset);
        const frag = tail.extractContents();
        newCode.textContent = '';
        if (frag && (frag.textContent || '').replace(/\u200b/g, '').trim()) {
            newCode.appendChild(frag);
        } else {
            newCode.appendChild(doc.createTextNode('\u200b'));
        }
        if (!(codeEl.textContent || '').replace(/\u200b/g, '').trim()) {
            codeEl.textContent = '';
            codeEl.appendChild(doc.createTextNode('\u200b'));
        }
    }

    const next = codeEl.nextSibling;
    if (next) {
        parent.insertBefore(br, next);
        parent.insertBefore(newCode, br.nextSibling);
    } else {
        parent.appendChild(br);
        parent.appendChild(newCode);
    }

    const nr = doc.createRange();
    const fc = newCode.firstChild;
    if (fc?.nodeType === Node.TEXT_NODE) {
        const len = (fc.textContent || '').length;
        nr.setStart(fc, len);
        nr.collapse(true);
    } else {
        nr.selectNodeContents(newCode);
        nr.collapse(false);
    }
    sel.removeAllRanges();
    sel.addRange(nr);
    return true;
}

function getComposeLinkHost(el, editorRoot) {
    if (!el || !editorRoot?.contains(el)) return null;
    const pre = el.closest?.('pre');
    if (pre && editorRoot.contains(pre)) return pre;
    const bq = el.closest?.('blockquote');
    if (bq && editorRoot.contains(bq)) return bq;
    const code = el.closest?.('code');
    if (code && isInlineCodeElement(code, editorRoot)) return code;
    return null;
}

/** If caret is inside plain text of host and on a URL, return safe href. */
function getUrlAtCaretInHost(host, selection) {
    if (!host || !selection?.rangeCount || !selection.isCollapsed) return null;
    const range = selection.getRangeAt(0);
    const n = range.startContainer;
    const off = range.startOffset;
    if (n.nodeType !== Node.TEXT_NODE || !host.contains(n)) return null;
    const text = n.textContent || '';
    const re = /\b(https?:\/\/[^\s<]+)|(www\.[^\s<]+)/gi;
    let m;
    while ((m = re.exec(text))) {
        const start = m.index;
        const raw = m[0];
        const end = start + raw.length;
        if (off >= start && off <= end) {
            const trimmed = trimTrailingPunctuation(raw);
            const href =
                /^https?:\/\//i.test(trimmed) ? trimmed : normalizePlainUrlCandidateToHref(trimmed);
            if (href && (href.startsWith('http://') || href.startsWith('https://'))) return href;
        }
    }
    return null;
}

function isInlineCodeElement(codeEl, editorRoot) {
    if (!codeEl || String(codeEl.tagName || '').toLowerCase() !== 'code') return false;
    if (!editorRoot?.contains(codeEl)) return false;
    return !codeEl.closest('pre');
}

function closestInlineCodeFromSelection(editorRoot, selection) {
    if (!editorRoot || !selection?.rangeCount) return null;
    const range = selection.getRangeAt(0);
    const nodes = [
        selection.anchorNode,
        selection.focusNode,
        range.startContainer,
        range.endContainer,
        range.commonAncestorContainer,
    ];
    for (const n of nodes) {
        if (!n || !editorRoot.contains(n)) continue;
        const el = n.nodeType === Node.TEXT_NODE ? n.parentElement : n;
        const code = el?.closest?.('code');
        if (isInlineCodeElement(code, editorRoot)) return code;
    }
    return null;
}

function unwrapInlineCodeElement(codeEl) {
    if (!codeEl?.parentNode) return;
    const parent = codeEl.parentNode;
    while (codeEl.firstChild) parent.insertBefore(codeEl.firstChild, codeEl);
    parent.removeChild(codeEl);
}

function moveCaretAfterNode(node) {
    if (!node?.parentNode || typeof document === 'undefined') return;
    const parent = node.parentNode;
    const doc = node.ownerDocument || document;
    const sel = document.getSelection();
    if (!sel) return;

    let next = node.nextSibling;
    while (next?.nodeType === Node.TEXT_NODE && !(next.textContent || '').length) {
        const dead = next;
        next = next.nextSibling;
        parent.removeChild(dead);
    }
    if (next?.nodeType === Node.TEXT_NODE) {
        const t = next.textContent || '';
        if (t.length > 0) {
            const onlyInvisibleAnchors = /^[\u200b\uFEFF]*$/.test(t);
            const pos = onlyInvisibleAnchors ? t.length : 0;
            const r = doc.createRange();
            r.setStart(next, Math.min(pos, t.length));
            r.collapse(true);
            sel.removeAllRanges();
            sel.addRange(r);
            return;
        }
    }

    const pad = doc.createTextNode('\u200b');
    parent.insertBefore(pad, next);
    const r = doc.createRange();
    r.setStart(pad, 1);
    r.collapse(true);
    sel.removeAllRanges();
    sel.addRange(r);
}

/** True when caret is collapsed at the very end of an inline <code> (not inside <pre>). */
function isCollapsedCaretAtEndOfInlineCode(editorRoot, sel) {
    if (!editorRoot || !sel?.rangeCount || !sel.isCollapsed) return false;
    const code = closestInlineCodeFromSelection(editorRoot, sel);
    if (!code || !isInlineCodeElement(code, editorRoot)) return false;
    const range = sel.getRangeAt(0);
    const n = range.startContainer;
    const off = range.startOffset;
    if (n.nodeType === Node.TEXT_NODE) {
        const t = n.textContent || '';
        if (off !== t.length) return false;
        let lastText = null;
        try {
            const walker = document.createTreeWalker(code, NodeFilter.SHOW_TEXT, null);
            let w;
            while ((w = walker.nextNode())) lastText = w;
        } catch {
            return false;
        }
        return lastText === n;
    }
    if (n.nodeType === Node.ELEMENT_NODE && n === code) {
        return off >= code.childNodes.length;
    }
    return false;
}

function getCaretRect(range) {
    if (!range || typeof document === 'undefined') return null;
    try {
        const r = range.getBoundingClientRect?.();
        // Treat (0,0,0,0) as unreliable (common in contentEditable).
        const looksUnreliable =
            !r ||
            (((r.top ?? 0) < 2 && (r.left ?? 0) < 2 && (r.bottom ?? 0) < 2 && (r.right ?? 0) < 2) ||
                (!r.width && !r.height));
        if (!looksUnreliable) return r;
    } catch {
        void 0;
    }
    // Robust fallback: insert a temporary marker span at the caret, measure, then remove.
    try {
        const marker = document.createElement('span');
        marker.setAttribute('data-caret-marker', '1');
        marker.style.display = 'inline-block';
        marker.style.width = '0px';
        marker.style.height = '1em';
        marker.style.verticalAlign = 'baseline';
        marker.appendChild(document.createTextNode('\u200b'));
        const r2 = range.cloneRange();
        r2.collapse(true);
        r2.insertNode(marker);
        const rect = marker.getBoundingClientRect();
        marker.parentNode?.removeChild(marker);
        const looksUnreliable =
            !rect ||
            (((rect.top ?? 0) < 2 && (rect.left ?? 0) < 2 && (rect.bottom ?? 0) < 2 && (rect.right ?? 0) < 2) ||
                (!rect.width && !rect.height));
        return looksUnreliable ? null : rect;
    } catch {
        return null;
    }
}

/** True when editor has non-whitespace content (trimmed markdown). */
function computeHasSendableEditorText(editorEl) {
    if (!editorEl) return false;
    const text = (editorEl.textContent ?? '').replace(/\u200b/g, '').trim();
    return text.length > 0;
}

const isDark = (theme) => theme?.colors?.background?.default === '#141414';

const EMOJI_LIST = [
    '😀',
    '😃',
    '😄',
    '😁',
    '😅',
    '😂',
    '🤣',
    '😊',
    '😇',
    '🙂',
    '😉',
    '😍',
    '🥰',
    '😘',
    '👍',
    '👋',
    '❤️',
    '🔥',
    '✨',
    '👍',
    '👎',
    '🙏',
    '💪',
    '👏',
    '🎉',
    '✅',
    '❌',
    '⚠️',
    '💡',
    '📌',
];

const TYPING_STOP_DELAY_MS = 2000;

const TOOLTIP_DELAY_MS = 0.4;

const isMacOS = () => typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);

const AttachMenuRoot = styled.div`
    width: 260px;
    padding: 8px;
    border-radius: 14px;
    border: 1px solid ${({ $dark }) => ($dark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)')};
    background: ${({ $dark }) => ($dark ? 'rgba(31,31,31,0.92)' : 'rgba(255,255,255,0.92)')};
    box-shadow: ${({ $dark }) => ($dark ? '0 18px 60px rgba(0,0,0,0.55)' : '0 18px 60px rgba(2,6,23,0.18)')};
    backdrop-filter: blur(10px);
`;

const AttachMenuSectionLabel = styled.div`
    padding: 6px 10px 8px;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.35px;
    text-transform: uppercase;
    color: ${({ $dark }) => ($dark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.45)')};
    user-select: none;
`;

const AttachMenuDivider = styled.div`
    height: 1px;
    margin: 6px 10px;
    background: ${({ $dark }) => ($dark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)')};
`;

const AttachMenuItem = styled.button`
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 10px;
    border-radius: 10px;
    border: none;
    background: transparent;
    cursor: pointer;
    color: ${({ $dark }) => ($dark ? 'rgba(255,255,255,0.92)' : 'rgba(0,0,0,0.82)')};
    font-size: 13px;
    font-weight: 700;
    text-align: left;

    &:hover {
        background: ${({ $dark }) => ($dark ? 'rgba(255,255,255,0.08)' : 'rgba(2,6,23,0.05)')};
    }
`;

const AttachMenuIcon = styled.span`
    width: 34px;
    height: 34px;
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: ${({ $dark }) => ($dark ? 'rgba(255,255,255,0.08)' : 'rgba(2,6,23,0.05)')};
    border: 1px solid ${({ $dark }) => ($dark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)')};
    color: ${({ $dark }) => ($dark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.70)')};
    font-size: 16px;
`;

/** Per-OS key labels for tooltip chips: mac uses ⌘, win uses Ctrl */
const HOTKEY_KEYS = {
    bold: { mac: ['⌘', 'B'], win: ['Ctrl', 'B'] },
    italic: { mac: ['⌘', 'I'], win: ['Ctrl', 'I'] },
    underline: { mac: ['⌘', 'U'], win: ['Ctrl', 'U'] },
    strikethrough: { mac: ['⌘', 'Shift', 'S'], win: ['Ctrl', 'Shift', 'S'] },
    link: { mac: ['⌘', 'K'], win: ['Ctrl', 'K'] },
    unorderedList: { mac: ['⌘', 'Shift', '7'], win: ['Ctrl', 'Shift', '7'] },
    orderedList: { mac: ['⌘', 'Shift', '8'], win: ['Ctrl', 'Shift', '8'] },
    codeBlock: { mac: ['⌘', 'E'], win: ['Ctrl', 'E'] },
    inlineCode: { mac: ['⌘', '`'], win: ['Ctrl', '`'] },
    blockquote: { mac: ['⌘', 'Shift', 'Q'], win: ['Ctrl', 'Shift', 'Q'] },
};

function TooltipTitleWithKeys({ label, keys }) {
    const keyList = Array.isArray(keys) ? keys : [];
    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '2px 0' }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.95)', lineHeight: 1.3 }}>
                {label}
            </span>
            {keyList.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', justifyContent: 'center' }}>
                    {keyList.map((k, i) => (
                        <span
                            key={i}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: 20,
                                height: 20,
                                padding: '0 5px',
                                fontSize: 10,
                                fontWeight: 600,
                                color: '#fff',
                                background: 'rgba(255,255,255,0.18)',
                                border: '1px solid rgba(255,255,255,0.25)',
                                borderRadius: 4,
                            }}
                        >
                            {k}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

/** Toolbar: block `<pre>` (whole line) — `</>` on a rounded “document” outline (product-style). */
function ComposeCodeBlockIcon() {
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18 }} aria-hidden>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
                <path
                    d="M8 20h10a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-5"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                <path
                    d="M8 20V7a2 2 0 0 1 2-2h3.2"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                <text
                    x="3.5"
                    y="13.2"
                    fill="currentColor"
                    fontSize="8.5"
                    fontWeight="700"
                    fontFamily="ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace"
                >
                    {'</>'}
                </text>
            </svg>
        </span>
    );
}

function ComposeInlineCodeIcon() {
    return (
        <span
            style={{
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace",
                fontSize: 13,
                fontWeight: 800,
                lineHeight: 1,
                letterSpacing: '-0.06em',
            }}
            aria-hidden
        >
            {'</>'}
        </span>
    );
}

const ATTACH_THUMB_SIZE = 56;

/** contentEditable: fenced code block (single-tone panel) vs blockquote (grey panel; Shift+Enter for line breaks). */
const ComposeEditorShell = styled.div`
    width: 100%;
    position: relative;
    display: flex;
    min-height: 0;
    .compose-editor-root {
        flex: 1;
        min-height: 32px;
        max-height: 160px;
        overflow-y: auto;
        border-radius: 0;
        padding: 6px 12px;
        border: none;
        background: transparent;
        color: ${({ $dark }) => ($dark ? 'rgba(255,255,255,0.9)' : '#111')};
        font-size: 14px;
        line-height: 1.45;
        outline: none;
        resize: none;
        box-sizing: border-box;
    }
    .compose-editor-root pre,
    .compose-editor-root pre.compose-pre {
        display: block;
        width: 100%;
        max-width: 100%;
        box-sizing: border-box;
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace;
        font-size: 12.5px;
        line-height: 1.45;
        margin: 0 0 8px 0;
        padding: 10px 12px;
        border-radius: 4px;
        border: 1px solid ${({ $dark }) => ($dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)')};
        background: ${({ $dark }) => ($dark ? '#1a1a1d' : '#f4f4f4')};
        color: ${({ $dark }) => ($dark ? 'rgba(235,235,235,0.88)' : '#1d1c1d')};
        overflow: visible;
        white-space: pre-wrap;
        word-break: break-word;
        overflow-wrap: anywhere;
    }
    .compose-editor-root pre code {
        font-family: inherit;
        background: transparent !important;
        padding: 0 !important;
        color: inherit !important;
        font-size: inherit;
    }
    /* Inline <code>: same family as body; neutral chip (no second hue inside fenced blocks). */
    .compose-editor-root code {
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace;
        font-size: 0.92em;
        line-height: 1.35;
        padding: 2px 6px;
        border-radius: 4px;
        border: 1px solid
            ${({ $dark }) => ($dark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.1)')};
        background: ${({ $dark }) =>
        $dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'} !important;
        color: ${({ $dark }) => ($dark ? 'rgba(255,255,255,0.88)' : '#374151')};
    }
    .compose-editor-root blockquote {
        margin: 0 0 8px 0;
        padding: 10px 12px;
        border-radius: 8px;
        box-sizing: border-box;
        max-width: 100%;
        min-width: 0;
        overflow-wrap: break-word;
        word-break: break-word;
        font-style: normal;
        font-family: inherit;
        font-size: 14px;
        line-height: 1.5;
        min-height: 2.5em;
        overflow: visible;
        border: 1px solid ${({ $dark }) => ($dark ? 'rgba(255,255,255,0.14)' : '#d1d5db')};
        border-left: 3px solid ${({ $dark }) => ($dark ? '#71717a' : '#9ca3af')};
        background: ${({ $dark }) => ($dark ? '#3f3f46' : '#e5e7eb')};
        color: ${({ $dark }) => ($dark ? 'rgba(255,255,255,0.94)' : '#1f2937')};
    }
    .compose-editor-root blockquote code {
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace;
        font-size: 0.92em;
        background: ${({ $dark }) =>
        $dark ? 'rgba(0,0,0,0.22)' : 'rgba(0,0,0,0.08)'} !important;
        color: inherit !important;
        border: 1px solid ${({ $dark }) => ($dark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)')};
        padding: 2px 5px;
        border-radius: 4px;
    }
    .compose-editor-root ul,
    .compose-editor-root ol {
        margin: 0 0 6px 0;
        padding-left: 1.85em;
        padding-inline-end: 12px;
        list-style-position: outside;
        box-sizing: border-box;
    }
    .compose-editor-root ul > li,
    .compose-editor-root ol > li {
        display: list-item;
        margin: 0 0 4px 0;
        padding-left: 0.25em;
        padding-inline-start: 0.35em;
        list-style-position: outside;
        list-style-type: inherit;
    }
    .compose-editor-root ul > li::marker,
    .compose-editor-root ol > li::marker {
        font-size: 0.85em;
        color: ${({ $dark }) => ($dark ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.55)')};
    }
    .compose-editor-root ol {
        list-style-type: decimal;
    }
`;

function isComposeVideoFile(file) {
    const t = String(file?.type || '').toLowerCase();
    if (t.startsWith('video/')) return true;
    const n = String(file?.name || '');
    return /\.(webm|mp4|mov|mkv|avi|m4v|mpeg|mpg)$/i.test(n);
}

/** Images and videos in compose strip order — used for one preview modal gallery. */
function getComposePreviewableAttachments(items) {
    return (items || []).filter((a) => {
        const f = a?.file;
        if (!f) return false;
        if (String(f.type || '').startsWith('image/')) return true;
        return isComposeVideoFile(f);
    });
}

function formatFileSize(bytes) {
    const b = Number(bytes);
    if (!Number.isFinite(b) || b <= 0) return '';
    const units = ['B', 'KB', 'MB', 'GB'];
    let v = b;
    let i = 0;
    while (v >= 1024 && i < units.length - 1) {
        v /= 1024;
        i += 1;
    }
    const fixed = i === 0 ? String(Math.round(v)) : v >= 10 ? v.toFixed(0) : v.toFixed(1);
    return `${fixed} ${units[i]}`;
}

function AttachmentThumb({ item, dark, onRemove, onPreview }) {
    const [hover, setHover] = useState(false);
    const type = item.file?.type || item.type || '';
    const isImage = type.startsWith('image/');
    const isVideo = isComposeVideoFile(item.file) || (item.isExisting && (type.includes('video') || /\.(mp4|webm|ogg|mov)$/i.test(item.url || '')));
    const showMediaPreview = isImage || isVideo;
    const iconSrc = !showMediaPreview ? getFileIconSrc({ name: item?.file?.name || item?.name, mimeType: type }) : null;
    const boxBg = dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
    const boxBorder = dark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.08)';
    return (
        <div
            role="presentation"
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
                position: 'relative',
                width: ATTACH_THUMB_SIZE,
                height: ATTACH_THUMB_SIZE,
                borderRadius: 10,
                background: boxBg,
                border: boxBorder,
                overflow: 'hidden',
                flexShrink: 0,
                cursor: showMediaPreview ? 'pointer' : 'default',
            }}
            onClick={() => {
                if (!showMediaPreview) return;
                onPreview?.(item);
            }}
        >
            <button
                type="button"
                aria-label="Remove"
                onClick={(e) => {
                    e.stopPropagation();
                    onRemove();
                }}
                style={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    zIndex: 1,
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    border: 'none',
                    background: dark ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.5)',
                    color: '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 10,
                    opacity: hover ? 1 : 0,
                    transition: 'opacity 0.2s ease',
                    pointerEvents: hover ? 'auto' : 'none',
                }}
            >
                <CloseOutlined />
            </button>
            {isImage && item.previewUrl ? (
                <img
                    src={item.previewUrl}
                    alt=""
                    style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                    }}
                />
            ) : isVideo && item.previewUrl ? (
                <video
                    src={item.previewUrl}
                    muted
                    playsInline
                    preload="metadata"
                    style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        pointerEvents: 'none',
                    }}
                />
            ) : (
                <span
                    style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    {iconSrc ? (
                        <img
                            src={iconSrc}
                            alt=""
                            style={{
                                width: 26,
                                height: 26,
                                objectFit: 'contain',
                                opacity: dark ? 0.92 : 0.88,
                            }}
                        />
                    ) : (
                        <FileOutlined
                            style={{
                                fontSize: 22,
                                color: dark ? 'rgba(255,255,255,0.5)' : '#888',
                            }}
                        />
                    )}
                </span>
            )}
        </div>
    );
}

export const MessageCompose = forwardRef(function MessageCompose(
    {
        onSend,
        placeholder,
        disabled,
        onKeyDown,
        toolbar,
        onAttachImage,
        onAttachDocument,
        onSnapPhoto,
        onTypingStart,
        onTypingStop,
        socketStatus,
        popupZIndex,
        mentionUsers = [],
        mentionMilestones = [],
        mentionSprints = [],
        hashtagTags = [],
        initialFiles = [],
    },
    ref,
) {
    const modalZIndex = typeof popupZIndex === 'number' ? popupZIndex + 50 : undefined;
    const theme = useTheme();
    const dark = isDark(theme);
    const primary = theme?.colors?.primary || theme?.colors?.background?.primary || '#8b5cf6';
    const editorRef = useRef(null);
    /** Second consecutive Shift+Enter inside the same pre/blockquote exits that block (first inserts a soft line). */
    const lastShiftEnterBlockElRef = useRef(null);
    const composeShellRef = useRef(null);
    const handleEditorInputRef = useRef(() => { });
    const fileInputRef = useRef(null);
    const documentInputRef = useRef(null);
    const videoInputRef = useRef(null);
    const audioInputRef = useRef(null);

    const [formatState, setFormatState] = useState({
        bold: false,
        italic: false,
        underline: false,
        strikethrough: false,
        insertUnorderedList: false,
        insertOrderedList: false,
        inPre: false,
        inInlineCode: false,
        inBlockquote: false,
    });
    const [isEmpty, setIsEmpty] = useState(true);
    const [hasSendableText, setHasSendableText] = useState(false);
    const [emojiOpen, setEmojiOpen] = useState(false);
    const [attachMenuOpen, setAttachMenuOpen] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    /** Image/video full preview modal: { kind, name, src, revokeOnClose?, galleryIds?, galleryIndex? } */
    const [attachmentPreview, setAttachmentPreview] = useState(null);
    const lastPreviewSrcRef = useRef(null);
    const [cameraOpen, setCameraOpen] = useState(false);
    const [cameraError, setCameraError] = useState(null);
    const [cameraLoading, setCameraLoading] = useState(false);
    const [videoRecordOpen, setVideoRecordOpen] = useState(false);
    const [videoRecordError, setVideoRecordError] = useState(null);
    const [videoRecordLoading, setVideoRecordLoading] = useState(false);
    const [isRecordingVideo, setIsRecordingVideo] = useState(false);
    const [attachedFiles, setAttachedFiles] = useState([]);
    const [pendingLinkAttachments, setPendingLinkAttachments] = useState([]);
    const [linkModalOpen, setLinkModalOpen] = useState(false);
    const [linkForm] = Form.useForm();
    const [isRecording, setIsRecording] = useState(false);
    const [recordingRequesting, setRecordingRequesting] = useState(false);
    const [recordingError, setRecordingError] = useState(null);
    const streamRef = useRef(null);
    const audioStreamRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);

    useEffect(() => {
        if (Array.isArray(initialFiles) && initialFiles.length > 0) {
            setAttachedFiles(prev => {
                // simple deep-ish check to prevent infinite loops if array looks the same
                if (prev.length === initialFiles.length && prev.every((p, i) => p.id === initialFiles[i].id)) return prev;
                return initialFiles.map(f => ({
                    id: f.id,
                    name: f.name,
                    type: f.type,
                    previewUrl: f.url,
                    url: f.url,
                    isExisting: true
                }));
            });
            setIsEmpty(false);
        } else {
            setAttachedFiles(prev => prev.length === 0 ? prev : []);
        }
    }, [initialFiles]);

    const typingStopTimerRef = useRef(null);
    const typingActiveRef = useRef(false);
    const [suggestOpen, setSuggestOpen] = useState(false);
    const [suggestType, setSuggestType] = useState(null); // 'mention' | 'tag'
    const [suggestQuery, setSuggestQuery] = useState('');
    const [suggestPos, setSuggestPos] = useState(null);
    const suggestRangeRef = useRef(null);
    const suggestHighlightIndexRef = useRef(0);
    const suggestDropdownRef = useRef(null);

    const mentionOptions = useMemo(() => {
        const users = (Array.isArray(mentionUsers) ? mentionUsers : [])
            .map((u) => ({
                type: 'user',
                id: u?.userId ?? u?.id,
                label: u?.name ?? u?.email ?? '',
                email: u?.email ?? '',
            }))
            .filter((u) => !!u.id && String(u.label || '').trim());

        const milestones = (Array.isArray(mentionMilestones) ? mentionMilestones : [])
            .map((m) => ({
                type: 'milestone',
                id: m?.milestoneId ?? m?.id,
                label: m?.name ?? 'Milestone',
            }))
            .filter((m) => !!m.id);

        const sprints = (Array.isArray(mentionSprints) ? mentionSprints : [])
            .map((s) => ({
                type: 'sprint',
                id: s?.sprintId ?? s?.id,
                label: s?.name ?? 'Sprint',
            }))
            .filter((s) => !!s.id);

        return [...users, ...milestones, ...sprints];
    }, [mentionUsers, mentionMilestones, mentionSprints]);

    const tagOptions = useMemo(() => {
        const list = Array.isArray(hashtagTags) ? hashtagTags : [];
        return list
            .map((t) => ({
                id: t?.id ?? t?.tag,
                tag: String(t?.tag ?? '').trim(),
            }))
            .filter((t) => t.tag.startsWith('#'));
    }, [hashtagTags]);

    const filteredSuggestItems = useMemo(() => {
        const q = String(suggestQuery || '').trim().toLowerCase();
        if (suggestType === 'mention') {
            const base = mentionOptions;
            if (!q) return base.slice(0, 8);
            return base
                .filter((u) => String(u.label || '').toLowerCase().includes(q) || String(u.email || '').toLowerCase().includes(q))
                .slice(0, 8);
        }
        if (suggestType === 'tag') {
            const base = tagOptions;
            if (!q) return base.slice(0, 10);
            return base
                .filter((t) => String(t.tag || '').toLowerCase().includes(`#${q}`) || String(t.tag || '').toLowerCase().includes(q))
                .slice(0, 10);
        }
        return [];
    }, [suggestQuery, suggestType, mentionOptions, tagOptions]);

    const closeSuggest = useCallback(() => {
        setSuggestOpen(false);
        setSuggestType(null);
        setSuggestQuery('');
        setSuggestPos(null);
        suggestRangeRef.current = null;
        suggestHighlightIndexRef.current = 0;
    }, []);

    const placeSuggestDropdown = useCallback((range, opts = {}) => {
        const { preferBelow = false } = opts || {};
        const caretRect = getCaretRect(range);

        const dropdownWidth = 360;
        const dropdownHeight = 270;
        const margin = 10;

        const vw =
            typeof window !== 'undefined'
                ? window.innerWidth || document.documentElement?.clientWidth || 0
                : 0;
        const vh =
            typeof window !== 'undefined'
                ? window.innerHeight || document.documentElement?.clientHeight || 0
                : 0;

        const editorBox = editorRef.current?.getBoundingClientRect?.() ?? null;
        if (!caretRect && (!editorBox || !(editorBox.width || editorBox.height))) {
            // If layout hasn't settled yet, retry once next frame instead of snapping to (margin, margin).
            if (typeof requestAnimationFrame !== 'undefined') {
                requestAnimationFrame(() => {
                    const eb = editorRef.current?.getBoundingClientRect?.() ?? null;
                    if (!eb) return;
                    const desiredLeft = (eb.left ?? 0) + 8;
                    const desiredAboveTop = (eb.top ?? 0) - dropdownHeight - 10;
                    // Chat UX: open UP only; if it would go off-screen, clamp.
                    const top = desiredAboveTop;
                    const maxLeft = Math.max(margin, vw - dropdownWidth - margin);
                    const maxTop = Math.max(margin, vh - dropdownHeight - margin);
                    setSuggestPos({
                        left: Math.max(margin, Math.min(maxLeft, desiredLeft)),
                        top: Math.max(margin, Math.min(maxTop, top)),
                    });
                });
            }
            return;
        }

        // If caret rect is unavailable/unreliable, anchor to the editor box so dropdown appears near the input.
        const baseLeft = caretRect?.left ?? editorBox?.left ?? 0;
        const baseTop = caretRect?.top ?? editorBox?.top ?? 0;
        const baseBottom = caretRect?.bottom ?? editorBox?.bottom ?? 0;

        const desiredLeft = baseLeft + 8;
        const desiredAboveTop = baseTop - dropdownHeight - 10;
        const desiredBelowTop = baseBottom + 10;
        // Chat UX: open UP by default, but for tiny mention lists (1–2) open DOWN for clarity.
        const top = preferBelow ? desiredBelowTop : desiredAboveTop;

        // Clamp to viewport.
        const maxLeft = Math.max(margin, vw - dropdownWidth - margin);
        const maxTop = Math.max(margin, vh - dropdownHeight - margin);
        const leftClamped = Math.max(margin, Math.min(maxLeft, desiredLeft));
        const topClamped = Math.max(margin, Math.min(maxTop, top));

        setSuggestPos({ top: topClamped, left: leftClamped });
    }, []);

    const tryOpenSuggestFromCaret = useCallback(() => {
        const root = editorRef.current;
        if (!root || typeof document === 'undefined') return;
        const sel = document.getSelection();
        if (!sel?.rangeCount) return;
        const range = sel.getRangeAt(0);
        if (!root.contains(range.startContainer)) return;

        // Use plain text from start to caret; match "@query" or "#query" right before caret.
        let textBefore = '';
        try {
            const r = range.cloneRange();
            r.setStart(root, 0);
            r.setEnd(range.startContainer, range.startOffset);
            textBefore = r.toString();
        } catch {
            textBefore = '';
        }
        const mAt = textBefore.match(/@([^\s@#]*)$/);
        const mHash = textBefore.match(/#([^\s@#]*)$/);

        // Prefer the last trigger if both somehow match.
        const atIndex = textBefore.lastIndexOf('@');
        const hashIndex = textBefore.lastIndexOf('#');
        const useAt = !!mAt && atIndex > hashIndex;
        const useHash = !!mHash && hashIndex > atIndex;

        if (!useAt && !useHash) {
            closeSuggest();
            return;
        }

        const type = useAt ? 'mention' : 'tag';
        const query = useAt ? (mAt?.[1] ?? '') : (mHash?.[1] ?? '');

        // If mention list is tiny (1–2), prefer opening DOWN (more natural + avoids covering recent messages).
        const preferBelow =
            type === 'mention' && (Array.isArray(mentionOptions) ? mentionOptions.length : 0) <= 2;

        // Ensure we always place the dropdown even if the caret rect fails.
        placeSuggestDropdown(range, { preferBelow });

        // Save a range spanning from trigger to caret by walking backwards in the current text node.
        try {
            const startNode = range.startContainer;
            if (startNode?.nodeType === Node.TEXT_NODE) {
                const full = startNode.nodeValue ?? '';
                const caret = range.startOffset ?? full.length;
                const slice = full.slice(0, caret);
                const trig = type === 'mention' ? slice.lastIndexOf('@') : slice.lastIndexOf('#');
                if (trig >= 0) {
                    const tokenRange = range.cloneRange();
                    tokenRange.setStart(startNode, trig);
                    suggestRangeRef.current = tokenRange;
                    setSuggestType(type);
                    setSuggestQuery(query);
                    setSuggestOpen(true);
                    return;
                }
            }
        } catch {
            void 0;
        }

        // Fallback: open but without replacement-range (will just insert at caret).
        suggestRangeRef.current = null;
        setSuggestType(type);
        setSuggestQuery(query);
        setSuggestOpen(true);
    }, [closeSuggest, placeSuggestDropdown]);

    const insertSuggestItem = useCallback(
        (item) => {
            const root = editorRef.current;
            if (!root || typeof document === 'undefined') return;
            root.focus();
            const sel = document.getSelection();
            if (!sel) return;

            const mentionType = item?.type ?? 'user';
            const mentionLabel = item?.label || item?.email || '';
            const mentionId = item?.id;
            const tagText = String(item?.tag || '').trim();
            const tagEncoded = tagText ? encodeURIComponent(tagText) : '';
            const insertHtml =
                suggestType === 'mention'
                    ? (() => {
                        const safeLabel = String(mentionLabel).replace(/</g, '&lt;').replace(/>/g, '&gt;');
                        const safeId = String(mentionId || '').trim().replace(/</g, '&lt;').replace(/>/g, '&gt;');
                        if (mentionType === 'milestone') {
                            // Mentions: do NOT append extra trailing space (especially noticeable when only 1 member).
                            return `<a href="#mention-milestone-${safeId}" data-mention="milestone" contenteditable="false">${safeLabel}</a>`;
                        }
                        if (mentionType === 'sprint') {
                            return `<a href="#mention-sprint-${safeId}" data-mention="sprint" contenteditable="false">${safeLabel}</a>`;
                        }
                        return `<a href="#mention-user-${safeId}" data-mention="user" contenteditable="false">@${safeLabel}</a>`;
                    })()
                    : `<a href="hashtag://tag?tag=${tagEncoded}" data-hashtag="${tagText}" contenteditable="false">${String(tagText).replace(/</g, '&lt;').replace(/>/g, '&gt;')}</a> `;

            const tokenRange = suggestRangeRef.current;
            if (tokenRange) {
                try {
                    tokenRange.deleteContents();
                    // Insert HTML chip-like link so it looks distinct while typing.
                    // Mentions: add leading/trailing spaces for better UX.
                    const finalHtml = ` ${insertHtml} \u200b`;
                    document.execCommand('insertHTML', false, finalHtml);
                    handleEditorInputRef.current?.();
                    closeSuggest();

                    // Force focus and caret position to ensure user can continue typing.
                    requestAnimationFrame(() => {
                        if (editorRef.current) {
                            editorRef.current.focus();
                            const s = document.getSelection();
                            if (s && s.rangeCount > 0) {
                                s.collapseToEnd();
                            }
                        }
                    });
                    return;
                } catch {
                    void 0;
                }
            }

            const finalHtml = ` ${insertHtml} \u200b`;
            document.execCommand('insertHTML', false, finalHtml);
            handleEditorInputRef.current?.();
            closeSuggest();

            requestAnimationFrame(() => {
                if (editorRef.current) {
                    editorRef.current.focus();
                    const s = document.getSelection();
                    if (s && s.rangeCount > 0) {
                        s.collapseToEnd();
                    }
                }
            });
        },
        [suggestType, closeSuggest],
    );
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const videoRecordStreamRef = useRef(null);
    const videoRecordRecorderRef = useRef(null);
    const videoRecordChunksRef = useRef([]);
    const videoRecordPreviewRef = useRef(null);

    const iconColor = dark ? 'rgba(255,255,255,0.6)' : '#666';
    const iconHoverColor = dark ? '#fff' : '#111';
    const iconHoverBg = dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
    const activeBg = dark ? 'rgba(139, 92, 246, 0.25)' : 'rgba(139, 92, 246, 0.15)';
    const activeColor = dark ? '#c4b5fd' : '#6d28d9';

    const updateFormatState = useCallback(() => {
        if (typeof document === 'undefined') return;
        const ed = editorRef.current;
        if (!ed) return;
        const sel = document.getSelection();
        let inPre = false;
        let inInlineCode = false;
        let inBlockquote = false;
        if (sel?.rangeCount) {
            const range = sel.getRangeAt(0);
            /** Selection has anchor/focus; Range has start/end — do not use range.anchorNode (undefined). */
            const candidates = [
                sel.anchorNode,
                sel.focusNode,
                range.startContainer,
                range.endContainer,
                range.commonAncestorContainer,
            ].filter((n) => n && ed.contains(n));
            const walkUp = (start) => {
                let n = start.nodeType === Node.TEXT_NODE ? start.parentElement : start;
                while (n && n !== ed) {
                    if (n.nodeType === Node.ELEMENT_NODE) {
                        const t = n.tagName?.toLowerCase();
                        if (t === 'pre') inPre = true;
                        if (t === 'blockquote') inBlockquote = true;
                        if (t === 'code' && isInlineCodeElement(n, ed)) inInlineCode = true;
                    }
                    n = n.parentElement;
                }
            };
            candidates.forEach(walkUp);
        }
        try {
            setFormatState({
                bold: document.queryCommandState('bold'),
                italic: document.queryCommandState('italic'),
                underline: document.queryCommandState('underline'),
                strikethrough: document.queryCommandState('strikethrough'),
                insertUnorderedList: document.queryCommandState('insertUnorderedList'),
                insertOrderedList: document.queryCommandState('insertOrderedList'),
                inPre,
                inInlineCode,
                inBlockquote,
            });
        } catch {
            void 0;
        }
    }, []);

    useEffect(() => {
        const doc = typeof document !== 'undefined' ? document : null;
        if (!doc) return;
        const onSelectionChange = () => {
            const ed = editorRef.current;
            if (!ed) return;
            const sel = document.getSelection();
            if (sel?.anchorNode && ed.contains(sel.anchorNode)) {
                updateFormatState();
                if (suggestOpen) {
                    tryOpenSuggestFromCaret();
                }
            }
        };
        doc.addEventListener('selectionchange', onSelectionChange);
        return () => doc.removeEventListener('selectionchange', onSelectionChange);
    }, [updateFormatState, suggestOpen, tryOpenSuggestFromCaret]);

    const handleEditorMouseUp = useCallback(
        (ev) => {
            const root = editorRef.current;
            if (!root || !root.contains(ev.target)) {
                updateFormatState();
                return;
            }
            requestAnimationFrame(() => {
                const sel = document.getSelection();
                if (sel?.rangeCount && sel.isCollapsed && sel.anchorNode && root.contains(sel.anchorNode)) {
                    const code = closestInlineCodeFromSelection(root, sel);
                    if (code && isInlineCodeElement(code, root)) {
                        const rect = code.getBoundingClientRect();
                        const pad = 10;
                        if (ev.clientX >= rect.right - pad && ev.clientX <= rect.right + 22) {
                            moveCaretAfterNode(code);
                        }
                    }
                }
                updateFormatState();
            });
        },
        [updateFormatState]
    );

    const handleComposerClick = useCallback(
        (ev) => {
            updateFormatState();
            if (disabled || ev.defaultPrevented || ev.button !== 0) return;
            const root = editorRef.current;
            if (!root || !root.contains(ev.target)) return;
            if (ev.metaKey || ev.ctrlKey) return;
            const raw = ev.target;
            const el = raw.nodeType === Node.TEXT_NODE ? raw.parentElement : raw;
            if (el?.closest?.('a[href]')) return;
            const sel = document.getSelection();
            if (!sel?.isCollapsed || !sel.rangeCount) return;
            const host = getComposeLinkHost(el, root);
            if (!host) return;
            const href = getUrlAtCaretInHost(host, sel);
            if (!href) return;
            ev.preventDefault();
            window.open(href, '_blank', 'noopener,noreferrer');
        },
        [disabled, updateFormatState],
    );

    const addAttachments = useCallback((files) => {
        if (!files?.length) return;
        const newItems = [...Array.from(files)].map((file) => {
            const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
            const isImage = file.type.startsWith('image/');
            const isVideo = isComposeVideoFile(file);
            const previewUrl = isImage || isVideo ? URL.createObjectURL(file) : null;
            return { id, file, previewUrl };
        });
        setAttachedFiles((prev) => [...prev, ...newItems]);
    }, []);

    const closeAttachmentPreview = useCallback(() => {
        setAttachmentPreview((cur) => {
            if (cur?.revokeOnClose && cur.src) {
                lastPreviewSrcRef.current = cur.src;
            } else {
                lastPreviewSrcRef.current = null;
            }
            return null;
        });
    }, []);

    const navigateAttachmentGallery = useCallback(
        (delta) => {
            setAttachmentPreview((prev) => {
                if (!prev?.galleryIds?.length) return prev;
                const n = prev.galleryIds.length;
                if (n <= 1) return prev;
                let nextIdx = prev.galleryIndex + delta;
                if (nextIdx < 0) nextIdx = n - 1;
                if (nextIdx >= n) nextIdx = 0;
                const nextId = prev.galleryIds[nextIdx];
                const att = attachedFiles.find((a) => a.id === nextId);
                if (!att?.file) return prev;

                if (prev.revokeOnClose && prev.src) {
                    URL.revokeObjectURL(prev.src);
                }

                let revokeOnClose = false;
                let src = att.previewUrl;
                if (!src) {
                    src = URL.createObjectURL(att.file);
                    revokeOnClose = true;
                }

                const mime = String(att.file.type || '');
                const kind = mime.startsWith('image/') ? 'image' : 'video';

                return {
                    ...prev,
                    galleryIndex: nextIdx,
                    kind,
                    name: att.file.name || (kind === 'video' ? 'Video' : 'Image'),
                    src,
                    revokeOnClose,
                };
            });
        },
        [attachedFiles],
    );

    const openAttachmentPreview = useCallback(
        (item) => {
            if (!item?.file) return;
            const file = item.file;
            const mime = String(file.type || '');
            const isImage = mime.startsWith('image/');
            const isVideo = isComposeVideoFile(file);
            if (!isImage && !isVideo) return;

            const mediaItems = getComposePreviewableAttachments(attachedFiles);
            const idx = mediaItems.findIndex((a) => a.id === item.id);
            if (idx < 0) return;
            const cur = mediaItems[idx];
            const curMime = String(cur.file.type || '');
            const kind = curMime.startsWith('image/') ? 'image' : 'video';

            let revokeOnClose = false;
            let src = cur.previewUrl;
            if (!src) {
                src = URL.createObjectURL(cur.file);
                revokeOnClose = true;
            }
            setAttachmentPreview({
                kind,
                name: cur.file.name || (kind === 'video' ? 'Video' : 'Image'),
                src,
                revokeOnClose,
                galleryIds: mediaItems.map((i) => i.id),
                galleryIndex: idx,
            });
        },
        [attachedFiles],
    );

    const removeAttachment = useCallback(
        (id) => {
            closeAttachmentPreview();
            setAttachedFiles((prev) => {
                const item = prev.find((a) => a.id === id);
                if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
                return prev.filter((a) => a.id !== id);
            });
        },
        [closeAttachmentPreview],
    );

    useEffect(() => {
        const ids = attachmentPreview?.galleryIds;
        if (!ids || ids.length <= 1) return;
        if (attachmentPreview?.kind === 'image') return;
        const onKey = (e) => {
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                navigateAttachmentGallery(-1);
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                navigateAttachmentGallery(1);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [attachmentPreview, navigateAttachmentGallery]);

    const composeImagePreviewUrls = useMemo(() => {
        if (!attachmentPreview?.galleryIds?.length || attachmentPreview.kind !== 'image') return [];
        const urls = [];
        for (const id of attachmentPreview.galleryIds) {
            const att = attachedFiles.find((a) => a.id === id);
            if (!att?.file || !String(att.file.type || '').startsWith('image/')) continue;
            if (att.previewUrl) urls.push(att.previewUrl);
        }
        return urls;
    }, [attachmentPreview, attachedFiles]);

    const composeImageStartIndex = useMemo(() => {
        if (!attachmentPreview?.galleryIds?.length || attachmentPreview.kind !== 'image') return 0;
        const curId = attachmentPreview.galleryIds[attachmentPreview.galleryIndex];
        let imageIdx = 0;
        for (let i = 0; i < attachmentPreview.galleryIds.length; i++) {
            const id = attachmentPreview.galleryIds[i];
            const att = attachedFiles.find((a) => a.id === id);
            if (!att?.file || !String(att.file.type || '').startsWith('image/')) continue;
            if (id === curId) return imageIdx;
            imageIdx += 1;
        }
        return 0;
    }, [attachmentPreview, attachedFiles]);

    const handleComposeImageSlide = useCallback(
        (imageIdx) => {
            setAttachmentPreview((prev) => {
                if (!prev?.galleryIds) return prev;
                const imageIds = prev.galleryIds.filter((id) => {
                    const att = attachedFiles.find((a) => a.id === id);
                    return att?.file && String(att.file.type || '').startsWith('image/');
                });
                const nextId = imageIds[imageIdx];
                if (!nextId) return prev;
                const gIdx = prev.galleryIds.indexOf(nextId);
                const att = attachedFiles.find((a) => a.id === nextId);
                if (!att?.file) return prev;

                if (prev.revokeOnClose && prev.src) {
                    URL.revokeObjectURL(prev.src);
                }

                let revokeOnClose = false;
                let nextSrc = att.previewUrl;
                if (!nextSrc) {
                    nextSrc = URL.createObjectURL(att.file);
                    revokeOnClose = true;
                }

                return {
                    ...prev,
                    galleryIndex: gIdx,
                    kind: 'image',
                    name: att.file.name || 'Image',
                    src: nextSrc,
                    revokeOnClose,
                };
            });
        },
        [attachedFiles],
    );

    const closeImagePreviewAndRevokeBlob = useCallback(() => {
        closeAttachmentPreview();
        window.setTimeout(() => {
            const s = lastPreviewSrcRef.current;
            if (s) URL.revokeObjectURL(s);
            lastPreviewSrcRef.current = null;
        }, 320);
    }, [closeAttachmentPreview]);

    useImperativeHandle(
        ref,
        () => ({
            getHtml: () => editorRef.current?.innerHTML?.trim() || '',
            getAttachedFiles: () => attachedFiles.filter(a => !a.isExisting).map((a) => a.file),
            getExistingAttachments: () => attachedFiles.filter(a => a.isExisting).map(a => ({
                id: a.id,
                name: a.name,
                type: a.type,
                url: a.url
            })),
            addFiles: (files) => {
                if (!files?.length) return;
                addAttachments(files);
                setIsEmpty(false);
                setHasSendableText(computeHasSendableEditorText(editorRef.current));
            },
            setPlainText: (text) => {
                const el = editorRef.current;
                const t = String(text ?? '');
                if (el) {
                    const html = storedChatBodyToComposeHtml(t);
                    if (html) el.innerHTML = html;
                    else el.innerText = t;
                    requestAnimationFrame(() => {
                        moveCaretToEndOfElement(el);
                        handleEditorInputRef.current?.();
                    });
                }
                const hasText = el ? computeHasSendableEditorText(el) : Boolean(t.trim());
                setIsEmpty(!hasText);
                setHasSendableText(hasText);
            },
            clear: () => {
                closeAttachmentPreview();
                if (editorRef.current) {
                    editorRef.current.innerHTML = '';
                    setIsEmpty(true);
                }
                closeSuggest();
                setEmojiOpen(false);
                setAttachMenuOpen(false);
                setFormatState({
                    bold: false,
                    italic: false,
                    underline: false,
                    strikethrough: false,
                    insertUnorderedList: false,
                    insertOrderedList: false,
                    inPre: false,
                    inInlineCode: false,
                    inBlockquote: false,
                });
                setAttachedFiles((prev) => {
                    prev.forEach((a) => {
                        if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
                    });
                    return [];
                });
                setPendingLinkAttachments([]);
                setHasSendableText(false);
            },
            focus: () => editorRef.current?.focus?.(),
            insertEmoji: (emoji) => {
                const el = editorRef.current;
                if (!el || typeof document === 'undefined') return;
                el.focus();
                try {
                    document.getSelection().getRangeAt(0);
                } catch (_) {
                    const range = document.createRange();
                    range.selectNodeContents(el);
                    range.collapse(true);
                    document.getSelection().removeAllRanges();
                    document.getSelection().addRange(range);
                }
                document.execCommand('insertText', false, emoji);
                setIsEmpty(false);
                setHasSendableText(computeHasSendableEditorText(el));
            },
        }),
        [attachedFiles, addAttachments, closeAttachmentPreview],
    );

    const handleEditorInput = useCallback(() => {
        const el = editorRef.current;
        const html = el?.innerHTML ?? '';
        setIsEmpty(!html || html === '<br>' || html === '<br/>');
        const hasText = computeHasSendableEditorText(el);
        setHasSendableText(hasText);
        updateFormatState();
        tryOpenSuggestFromCaret();
        if (onTypingStart || onTypingStop) {
            if (typingStopTimerRef.current) clearTimeout(typingStopTimerRef.current);
            if (!typingActiveRef.current && (html || '').replace(/<[^>]+>/g, '').trim()) {
                typingActiveRef.current = true;
                onTypingStart?.();
            }
            typingStopTimerRef.current = setTimeout(() => {
                typingStopTimerRef.current = null;
                if (typingActiveRef.current) {
                    typingActiveRef.current = false;
                    onTypingStop?.();
                }
            }, TYPING_STOP_DELAY_MS);
        }
    }, [onTypingStart, onTypingStop, updateFormatState, tryOpenSuggestFromCaret]);

    useEffect(() => {
        handleEditorInputRef.current = handleEditorInput;
    }, [handleEditorInput]);

    const focusComposerAfterFilePick = useCallback(() => {
        setAttachMenuOpen(false);
        if (disabled) return;
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const el = editorRef.current;
                if (!el) return;
                moveCaretToEndOfElement(el);
                handleEditorInputRef.current?.();
            });
        });
    }, [disabled]);

    useEffect(() => {
        return () => {
            if (typingStopTimerRef.current) clearTimeout(typingStopTimerRef.current);
            if (typingActiveRef.current) onTypingStop?.();
        };
    }, [onTypingStop]);

    const handleEditorPaste = (e) => {
        e.preventDefault();
        const cd = e.clipboardData;
        const items = cd?.items ? Array.from(cd.items) : [];
        const fileItem = items.find((it) => (it?.type || '').startsWith('image/'));
        if (fileItem) {
            const blob = fileItem.getAsFile?.();
            if (blob) {
                const ext = (blob.type || '').split('/')[1] || 'png';
                const file = new File([blob], `screenshot-${Date.now()}.${ext}`, { type: blob.type || 'image/png' });
                addAttachments([file]);
                onAttachImage?.({ target: { files: [file] } });
                setIsEmpty(false);
                setHasSendableText(computeHasSendableEditorText(editorRef.current));
                return;
            }
        }
        const text = cd?.getData?.('text/plain') ?? '';
        if (text) document.execCommand('insertText', false, text);
        handleEditorInput();
    };

    const handleComposerDrop = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            setDragOver(false);
            const files = e.dataTransfer?.files ? Array.from(e.dataTransfer.files) : [];
            if (files.length === 0) return;
            addAttachments(files);
            const hasImage = files.some((f) => (f?.type || '').startsWith('image/'));
            if (hasImage) onAttachImage?.({ target: { files } });
            else onAttachDocument?.({ target: { files } });
            setIsEmpty(false);
            setHasSendableText(computeHasSendableEditorText(editorRef.current));
        },
        [addAttachments, onAttachImage, onAttachDocument],
    );

    const applyFormat = (cmd, value = null) => {
        editorRef.current?.focus();
        document.execCommand(cmd, false, value);
        updateFormatState();
    };

    const applyFormatBlockTag = (tagName) => {
        editorRef.current?.focus();
        const lower = tagName.toLowerCase();
        const variants = [`<${lower}>`, lower, lower.toUpperCase()];
        let ok = false;
        for (const v of variants) {
            try {
                if (document.execCommand('formatBlock', false, v)) {
                    ok = true;
                    break;
                }
            } catch {
                void 0;
            }
        }
        if (!ok) {
            try {
                if (lower === 'blockquote') {
                    document.execCommand('insertHTML', false, '<blockquote><br></blockquote>');
                } else if (lower === 'pre') {
                    document.execCommand(
                        'insertHTML',
                        false,
                        '<pre class="compose-pre"><code>&#8203;</code></pre>',
                    );
                }
            } catch {
                void 0;
            }
        }
        updateFormatState();
    };

    const togglePre = useCallback(() => {
        const root = editorRef.current;
        if (!root) return;
        root.focus();
        const pre = closestTagFromSelection(root, 'pre');
        if (pre) {
            unwrapBlockElement(pre);
            handleEditorInputRef.current?.();
            updateFormatState();
            return;
        }
        applyFormatBlockTag('pre');
    }, [applyFormatBlockTag, updateFormatState]);

    const toggleBlockquote = useCallback(() => {
        const root = editorRef.current;
        if (!root) return;
        root.focus();
        const bq = closestTagFromSelection(root, 'blockquote');
        if (bq) {
            unwrapBlockElement(bq);
            handleEditorInputRef.current?.();
            updateFormatState();
            return;
        }
        applyFormatBlockTag('blockquote');
    }, [applyFormatBlockTag, updateFormatState]);

    const toggleInlineCode = useCallback(() => {
        const root = editorRef.current;
        if (!root || typeof document === 'undefined') return;
        if (closestTagFromSelection(root, 'pre')) return;
        root.focus();
        const sel = document.getSelection();
        if (!sel?.rangeCount) return;
        const range = sel.getRangeAt(0);
        const existing = closestInlineCodeFromSelection(root, sel);

        if (existing) {
            const fullText = (existing.textContent || '').replace(/\u200b/g, '');
            const selText = (range.toString() || '').replace(/\u200b/g, '');
            if (sel.isCollapsed || (selText && selText === fullText)) {
                unwrapInlineCodeElement(existing);
                handleEditorInputRef.current?.();
                updateFormatState();
                return;
            }
        }

        try {
            if (sel.isCollapsed) {
                document.execCommand('insertHTML', false, '<code>\u200b</code>');
            } else {
                const extracted = range.extractContents();
                const code = document.createElement('code');
                code.appendChild(extracted);
                if (!code.textContent) code.appendChild(document.createTextNode('\u200b'));
                range.insertNode(code);
                sel.removeAllRanges();
                const nr = document.createRange();
                nr.selectNodeContents(code);
                nr.collapse(false);
                sel.addRange(nr);
            }
        } catch {
            try {
                document.execCommand('insertHTML', false, '<code>\u200b</code>');
            } catch {
                void 0;
            }
        }
        handleEditorInputRef.current?.();
        updateFormatState();
    }, [updateFormatState]);

    const tooltipProps = {
        mouseEnterDelay: TOOLTIP_DELAY_MS,
        ...(typeof popupZIndex === 'number' ? { zIndex: popupZIndex } : {}),
        styles: {
            body: {
                fontSize: 11,
                padding: '8px 12px',
                background: '#1f1f1f',
                color: 'rgba(255,255,255,0.95)',
                borderRadius: 8,
            },
        },
    };
    const getKeysForOs = (keyId) => {
        const entry = HOTKEY_KEYS[keyId];
        if (!entry) return [];
        return isMacOS() ? entry.mac : entry.win;
    };
    const toolbarBtn = (cmd, Icon, label, keyId, active) => {
        const base = {
            padding: '6px 8px',
            border: 'none',
            background: active ? activeBg : 'transparent',
            color: active ? activeColor : iconColor,
            cursor: 'pointer',
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 16,
        };
        const keys = getKeysForOs(keyId);
        return (
            <Tooltip
                key={label}
                title={<TooltipTitleWithKeys label={label} keys={keys} />}
                {...tooltipProps}
            >
                <button
                    type="button"
                    aria-label={label}
                    style={base}
                    onMouseEnter={(e) => {
                        if (!active) {
                            e.currentTarget.style.color = iconHoverColor;
                            e.currentTarget.style.background = iconHoverBg;
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (!active) {
                            e.currentTarget.style.color = iconColor;
                            e.currentTarget.style.background = 'transparent';
                        }
                    }}
                    onClick={() => applyFormat(cmd)}
                >
                    <Icon />
                </button>
            </Tooltip>
        );
    };

    const stopCameraStream = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
    }, []);

    const openCameraModal = useCallback(() => {
        setCameraOpen(true);
        setCameraError(null);
        setCameraLoading(true);
    }, []);

    useEffect(() => {
        if (!cameraOpen) return;
        let cancelled = false;
        const startCamera = async () => {
            try {
                if (!navigator.mediaDevices?.getUserMedia) {
                    setCameraError('Camera not supported in this browser.');
                    setCameraLoading(false);
                    return;
                }
                let stream;
                try {
                    stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
                } catch (_) {
                    stream = await navigator.mediaDevices.getUserMedia({ video: true });
                }
                if (cancelled) {
                    stream.getTracks().forEach((t) => t.stop());
                    return;
                }
                streamRef.current = stream;
                setCameraError(null);
                setCameraLoading(false);
            } catch (err) {
                if (cancelled) return;
                streamRef.current = null;
                setCameraLoading(false);
                if (err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError') {
                    setCameraError('Camera permission denied. Please allow camera access and try again.');
                } else if (err?.name === 'NotFoundError') {
                    setCameraError('No camera found.');
                } else {
                    setCameraError(err?.message || 'Could not access camera.');
                }
            }
        };
        startCamera();
        return () => {
            cancelled = true;
            stopCameraStream();
        };
    }, [cameraOpen, stopCameraStream]);

    useEffect(() => {
        if (!cameraOpen || cameraLoading || cameraError || !streamRef.current) return;
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = streamRef.current;
        video.play().catch(() => { });
    }, [cameraOpen, cameraLoading, cameraError]);

    const setVideoRef = useCallback((el) => {
        videoRef.current = el;
        if (el && streamRef.current) {
            el.srcObject = streamRef.current;
            el.play().catch(() => { });
        }
    }, []);

    const closeCameraModal = useCallback(() => {
        setCameraOpen(false);
        setCameraError(null);
        stopCameraStream();
    }, [stopCameraStream]);

    const stopVideoRecordStream = useCallback(() => {
        if (videoRecordStreamRef.current) {
            videoRecordStreamRef.current.getTracks().forEach((t) => t.stop());
            videoRecordStreamRef.current = null;
        }
        videoRecordRecorderRef.current = null;
    }, []);

    const openVideoRecordModal = useCallback(() => {
        setVideoRecordOpen(true);
        setVideoRecordError(null);
        setVideoRecordLoading(true);
        setIsRecordingVideo(false);
    }, []);

    const closeVideoRecordModal = useCallback(() => {
        if (videoRecordRecorderRef.current && videoRecordRecorderRef.current.state !== 'inactive') {
            videoRecordRecorderRef.current.stop();
        }
        stopVideoRecordStream();
        setVideoRecordOpen(false);
        setVideoRecordError(null);
        setIsRecordingVideo(false);
    }, [stopVideoRecordStream]);

    useEffect(() => {
        if (!videoRecordOpen) return;
        let cancelled = false;
        const startStream = async () => {
            try {
                if (!navigator.mediaDevices?.getUserMedia) {
                    setVideoRecordError('Video recording not supported in this browser.');
                    setVideoRecordLoading(false);
                    return;
                }
                const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
                if (cancelled) {
                    stream.getTracks().forEach((t) => t.stop());
                    return;
                }
                videoRecordStreamRef.current = stream;
                setVideoRecordError(null);
                setVideoRecordLoading(false);
                const video = videoRecordPreviewRef.current;
                if (video) {
                    video.srcObject = stream;
                    video.play().catch(() => { });
                }
            } catch (err) {
                if (cancelled) return;
                videoRecordStreamRef.current = null;
                setVideoRecordLoading(false);
                if (err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError') {
                    setVideoRecordError('Camera and microphone access denied. Allow access to record video.');
                } else if (err?.name === 'NotFoundError') {
                    setVideoRecordError('No camera or microphone found.');
                } else {
                    setVideoRecordError(err?.message || 'Could not access camera.');
                }
            }
        };
        startStream();
        return () => {
            cancelled = true;
            stopVideoRecordStream();
        };
    }, [videoRecordOpen, stopVideoRecordStream]);

    useEffect(() => {
        if (!videoRecordOpen || videoRecordLoading || videoRecordError || !videoRecordStreamRef.current) return;
        const video = videoRecordPreviewRef.current;
        if (!video) return;
        video.srcObject = videoRecordStreamRef.current;
        video.play().catch(() => { });
    }, [videoRecordOpen, videoRecordLoading, videoRecordError]);

    const startVideoRecording = useCallback(() => {
        const stream = videoRecordStreamRef.current;
        if (!stream || isRecordingVideo) return;
        videoRecordChunksRef.current = [];
        const mimeType = MediaRecorder.isTypeSupported('video/webm') ? 'video/webm' : 'video/mp4';
        const recorder = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported('video/webm') ? 'video/webm' : undefined });
        videoRecordRecorderRef.current = recorder;
        recorder.ondataavailable = (e) => {
            if (e.data.size > 0) videoRecordChunksRef.current.push(e.data);
        };
        recorder.onstop = () => {
            const chunks = videoRecordChunksRef.current;
            const blob = chunks.length ? new Blob(chunks, { type: recorder.mimeType || 'video/webm' }) : null;
            if (blob && blob.size > 0) {
                const ext = (recorder.mimeType || '').includes('webm') ? 'webm' : 'mp4';
                const file = new File([blob], `video-${Date.now()}.${ext}`, { type: blob.type });
                addAttachments([file]);
            }
            setIsRecordingVideo(false);
            if (videoRecordStreamRef.current) {
                videoRecordStreamRef.current.getTracks().forEach((t) => t.stop());
                videoRecordStreamRef.current = null;
            }
            videoRecordRecorderRef.current = null;
            setVideoRecordOpen(false);
            setVideoRecordError(null);
        };
        recorder.start(100);
        setIsRecordingVideo(true);
    }, [isRecordingVideo, addAttachments]);

    const stopVideoRecording = useCallback(() => {
        const recorder = videoRecordRecorderRef.current;
        if (recorder && recorder.state !== 'inactive') recorder.stop();
    }, []);

    const capturePhoto = useCallback(() => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas || !streamRef.current || video.readyState !== 4) return;
        const w = video.videoWidth;
        const h = video.videoHeight;
        if (w === 0 || h === 0) return;
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0);
        canvas.toBlob(
            (blob) => {
                if (!blob) return;
                const file = new File([blob], `capture-${Date.now()}.jpg`, { type: 'image/jpeg' });
                addAttachments([file]);
                onSnapPhoto?.({ target: { files: [file] } });
                closeCameraModal();
            },
            'image/jpeg',
            0.9,
        );
    }, [onSnapPhoto, closeCameraModal, addAttachments]);

    const handleAttachMenuClick = useCallback(({ key }) => {
        setAttachMenuOpen(false);
        if (key === 'upload-image') fileInputRef.current?.click();
        else if (key === 'upload-video') videoInputRef.current?.click();
        else if (key === 'upload-audio') audioInputRef.current?.click();
    }, []);

    const attachMenuItems = useMemo(() => {
        return [
            { key: 'upload-image', icon: <PictureOutlined />, label: 'Upload Image' },
            { key: 'upload-video', icon: <CloudUploadOutlined />, label: 'Upload Video' },
            { key: 'upload-audio', icon: <CustomerServiceOutlined />, label: 'Upload Audio' },
        ];
    }, []);

    const attachMenuOverlay = useMemo(() => {
        return (
            <AttachMenuRoot $dark={dark} onMouseDown={(e) => e.preventDefault()}>
                <AttachMenuSectionLabel $dark={dark}>Upload</AttachMenuSectionLabel>
                {attachMenuItems.map((it) => (
                    <AttachMenuItem
                        key={it.key}
                        type="button"
                        $dark={dark}
                        onClick={() => handleAttachMenuClick({ key: it.key })}
                    >
                        <AttachMenuIcon $dark={dark}>{it.icon}</AttachMenuIcon>
                        <span style={{ flex: 1, minWidth: 0 }}>{it.label}</span>
                    </AttachMenuItem>
                ))}
                <AttachMenuDivider $dark={dark} />
            </AttachMenuRoot>
        );
    }, [attachMenuItems, dark, handleAttachMenuClick]);

    const canSend =
        !!onSend &&
        !disabled &&
        (hasSendableText || attachedFiles.length > 0 || pendingLinkAttachments.length > 0);

    const handleSendClick = () => {
        if (!onSend || disabled) return;
        if (isRecording) {
            stopRecording();
            return;
        }
        const html = (editorRef.current?.innerHTML ?? '').trim() || '';
        const markdown = htmlToMarkdown(html);
        const text = markdown.trim();
        const hasText = text.length > 0 && html !== '<br>' && html !== '<br/>';

        const newFiles = attachedFiles.filter((a) => !a.isExisting).map((a) => a.file);
        const existingLinks = attachedFiles
            .filter((a) => a.isExisting)
            .map((a) => ({
                id: a.id,
                name: a.name,
                type: a.type,
                url: a.url,
                isExisting: true,
            }));
        const userLinks = pendingLinkAttachments.map(({ url, title }) => ({ url, title: title || url }));

        const finalLinks = [...existingLinks, ...userLinks];
        const hasContent = hasText || newFiles.length > 0 || finalLinks.length > 0;
        if (!hasContent) return;

        onSend(markdown, newFiles, finalLinks);
        editorRef.current.innerHTML = '';
        setIsEmpty(true);
        setHasSendableText(false);
        setFormatState({
            bold: false,
            italic: false,
            underline: false,
            strikethrough: false,
            insertUnorderedList: false,
            insertOrderedList: false,
            inPre: false,
            inInlineCode: false,
            inBlockquote: false,
        });
        setAttachedFiles((prev) => {
            prev.forEach((a) => {
                if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
            });
            return [];
        });
        setPendingLinkAttachments([]);
    };

    const openLinkModal = useCallback(() => {
        linkForm.resetFields();
        setLinkModalOpen(true);
    }, [linkForm]);

    const handleLinkModalSubmit = useCallback(() => {
        linkForm.validateFields().then((values) => {
            let url = (values.url || '').trim();
            const title = (values.title || '').trim();
            if (!url) return;
            if (!/^https?:\/\//i.test(url) && !/^mailto:/i.test(url)) {
                if (url.startsWith('//') && url.length > 2) {
                    void 0;
                } else if (/^[\w.-]+\.[a-z]{2,}([/?#].*)?$/i.test(url)) {
                    url = `https://${url}`;
                }
            }
            const label = title || url;
            setPendingLinkAttachments((prev) => [
                ...prev,
                { id: `link-${Date.now()}`, url, title: label, addedAt: Date.now() },
            ]);
            setIsEmpty(false);
            setLinkModalOpen(false);
            linkForm.resetFields();
        });
    }, [linkForm]);

    const removePendingLink = useCallback((id) => {
        setPendingLinkAttachments((prev) => prev.filter((p) => p.id !== id));
    }, []);

    const handleKeyDownEditor = (e) => {
        if (suggestOpen && (e.key === 'Escape' || e.key === 'Esc')) {
            e.preventDefault();
            closeSuggest();
            return;
        }
        if (suggestOpen && (e.key === 'Enter' || e.key === 'Tab')) {
            if (e.isComposing) return;
            e.preventDefault();
            const list = filteredSuggestItems;
            const idx = Math.max(0, Math.min(suggestHighlightIndexRef.current, list.length - 1));
            const chosen = list[idx];
            if (chosen) insertSuggestItem(chosen);
            else closeSuggest();
            return;
        }
        if (suggestOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            e.preventDefault();
            const delta = e.key === 'ArrowDown' ? 1 : -1;
            const max = Math.max(0, filteredSuggestItems.length - 1);
            const next = Math.max(0, Math.min(max, (suggestHighlightIndexRef.current ?? 0) + delta));
            suggestHighlightIndexRef.current = next;
            // trigger re-render for highlight by updating query (no-op)
            setSuggestQuery((q) => q);
            return;
        }
        if (!(e.key === 'Enter' && e.shiftKey)) {
            lastShiftEnterBlockElRef.current = null;
        }
        const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
        const mod = isMac ? e.metaKey : e.ctrlKey;

        const root = editorRef.current;
        if (root && !e.isComposing && !suggestOpen) {
            const sel = document.getSelection();
            if (sel?.rangeCount && sel.anchorNode && root.contains(sel.anchorNode)) {
                if (e.key === 'ArrowRight') {
                    if (isCollapsedCaretAtEndOfInlineCode(root, sel)) {
                        const code = closestInlineCodeFromSelection(root, sel);
                        if (code) {
                            e.preventDefault();
                            moveCaretAfterNode(code);
                            updateFormatState();
                            return;
                        }
                    }
                }
                /* Do not move caret out of inline code on printable keys — that made "a" stay in the chip and "sas" outside. Exit via ArrowRight or the mouse-up zone after the chip. */
            }
        }

        if (mod && e.key === 'b') {
            e.preventDefault();
            applyFormat('bold');
            return;
        }
        if (mod && e.key === 'i') {
            e.preventDefault();
            applyFormat('italic');
            return;
        }
        if (mod && e.key === 'u') {
            e.preventDefault();
            applyFormat('underline');
            return;
        }
        if (mod && e.shiftKey && e.key === 'S') {
            e.preventDefault();
            applyFormat('strikethrough');
            return;
        }
        if (mod && e.key === 'k') {
            e.preventDefault();
            openLinkModal();
            return;
        }
        if (mod && e.shiftKey && e.key === '7') {
            e.preventDefault();
            applyFormat('insertUnorderedList');
            return;
        }
        if (mod && e.shiftKey && e.key === '8') {
            e.preventDefault();
            applyFormat('insertOrderedList');
            return;
        }
        if (mod && (e.key === 'e' || e.key === 'E')) {
            e.preventDefault();
            togglePre();
            return;
        }
        if (mod && !e.shiftKey && (e.key === '`' || e.code === 'Backquote')) {
            e.preventDefault();
            toggleInlineCode();
            return;
        }
        if (mod && e.shiftKey && (e.key === 'q' || e.key === 'Q')) {
            e.preventDefault();
            applyFormatBlockTag('blockquote');
            return;
        }
        /** Shift+Enter: newline; second time in same pre/blockquote exits to body; in inline code starts a new chip line. */
        if (e.key === 'Enter' && e.shiftKey) {
            if (e.isComposing) return;
            e.preventDefault();
            const root = editorRef.current;
            if (!root) return;
            root.focus();
            const sel = document.getSelection();
            const pre = closestTagFromSelection(root, 'pre');
            if (pre) {
                if (lastShiftEnterBlockElRef.current === pre) {
                    exitBlockElementToBodyAfter(pre);
                    lastShiftEnterBlockElRef.current = null;
                } else {
                    insertSoftBreakAtCaret(root);
                    lastShiftEnterBlockElRef.current = pre;
                }
                handleEditorInput();
                updateFormatState();
                return;
            }
            const bq = getBlockquoteFromSelection(root, sel);
            if (bq) {
                if (lastShiftEnterBlockElRef.current === bq) {
                    exitBlockElementToBodyAfter(bq);
                    lastShiftEnterBlockElRef.current = null;
                } else {
                    insertSoftBreakAtCaret(root);
                    lastShiftEnterBlockElRef.current = bq;
                }
                handleEditorInput();
                updateFormatState();
                return;
            }
            const code = sel?.rangeCount ? closestInlineCodeFromSelection(root, sel) : null;
            if (code && isInlineCodeElement(code, root)) {
                shiftEnterNewInlineCodeInstance(code, root);
                lastShiftEnterBlockElRef.current = null;
                handleEditorInput();
                updateFormatState();
                return;
            }
            lastShiftEnterBlockElRef.current = null;
            insertSoftBreakAtCaret(root);
            handleEditorInput();
            updateFormatState();
            return;
        }
        /** Enter inside a code block (`<pre>`): new line (Slack-like). Enter otherwise sends. */
        if (e.key === 'Enter' && !e.shiftKey) {
            if (e.isComposing) return;
            const rootEnter = editorRef.current;
            if (rootEnter && closestTagFromSelection(rootEnter, 'pre')) {
                e.preventDefault();
                rootEnter.focus();
                lastShiftEnterBlockElRef.current = null;
                insertSoftBreakAtCaret(rootEnter);
                handleEditorInput();
                return;
            }
            e.preventDefault();
            handleSendClick();
            return;
        }
        onKeyDown?.(e);
    };

    const startRecording = useCallback(async () => {
        if (disabled || isRecording || recordingRequesting) return;
        setRecordingError(null);
        setRecordingRequesting(true);
        try {
            if (!navigator.mediaDevices?.getUserMedia) {
                setRecordingError('Recording not supported in this browser');
                return;
            }
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            setRecordingRequesting(false);
            audioStreamRef.current = stream;
            audioChunksRef.current = [];
            const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/ogg';
            const recorder = new MediaRecorder(stream);
            mediaRecorderRef.current = recorder;
            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) audioChunksRef.current.push(e.data);
            };
            recorder.onstop = () => {
                const chunks = audioChunksRef.current;
                const blob = chunks.length ? new Blob(chunks, { type: recorder.mimeType || mimeType }) : new Blob([], { type: mimeType });
                const ext = (recorder.mimeType || mimeType).includes('webm') ? 'webm' : 'ogg';
                const file = new File([blob], `voice.${ext}`, { type: blob.type });
                if (onSend && (chunks.length || blob.size > 0)) onSend('', [file]);
                stream.getTracks().forEach((t) => t.stop());
                audioStreamRef.current = null;
                mediaRecorderRef.current = null;
            };
            recorder.start(100);
            setIsRecording(true);
        } catch (err) {
            setRecordingRequesting(false);
            const name = err?.name || '';
            if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
                setRecordingError('Microphone access denied. Allow microphone in your browser to record.');
            } else if (name === 'NotFoundError') {
                setRecordingError('No microphone found.');
            } else {
                setRecordingError(err?.message || 'Could not access microphone');
            }
        }
    }, [disabled, isRecording, recordingRequesting, onSend]);

    const stopRecording = useCallback(() => {
        if (!isRecording) return;
        const recorder = mediaRecorderRef.current;
        if (recorder && recorder.state !== 'inactive') recorder.stop();
        setIsRecording(false);
        setRecordingError(null);
    }, [isRecording]);

    const handleMicClick = useCallback((e) => {
        e?.preventDefault?.();
        e?.stopPropagation?.();
        if (isRecording) stopRecording();
        else startRecording();
    }, [isRecording, startRecording, stopRecording]);

    useEffect(() => {
        return () => {
            if (audioStreamRef.current) {
                audioStreamRef.current.getTracks().forEach((t) => t.stop());
                audioStreamRef.current = null;
            }
            mediaRecorderRef.current = null;
        };
    }, []);

    const attachmentBtnStyle = {
        padding: '6px 10px',
        border: 'none',
        background: 'transparent',
        color: iconColor,
        cursor: 'pointer',
        borderRadius: 6,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 16,
    };
    const hoverIn = (e) => {
        e.currentTarget.style.color = iconHoverColor;
        e.currentTarget.style.background = iconHoverBg;
    };
    const hoverOut = (e) => {
        e.currentTarget.style.color = iconColor;
        e.currentTarget.style.background = 'transparent';
    };

    const toolBtnBase = (active) => ({
        padding: '6px 8px',
        border: 'none',
        background: active ? activeBg : 'transparent',
        color: active ? activeColor : iconColor,
        cursor: 'pointer',
        borderRadius: 6,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 16,
    });

    const sendBtnGradientStyle = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 40,
        height: 40,
        borderRadius: 8,
        background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
        color: '#fff',
        cursor: 'pointer',
        flexShrink: 0,
        border: 'none',
    };

    const insertEmojiAndClose = (emoji) => {
        const el = editorRef.current;
        if (el && typeof document !== 'undefined') {
            el.focus();
            document.execCommand('insertText', false, emoji);
            setIsEmpty(false);
        }
        setEmojiOpen(false);
    };
    const emojiDropdown = (
        <div
            style={{
                padding: 8,
                maxWidth: 280,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 4,
                background: dark ? '#1f1f1f' : '#fff',
                borderRadius: 8,
                boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
            }}
        >
            {EMOJI_LIST.map((emoji) => (
                <button
                    key={emoji}
                    type="button"
                    style={{
                        width: 32,
                        height: 32,
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        fontSize: 18,
                        borderRadius: 4,
                    }}
                    onClick={() => insertEmojiAndClose(emoji)}
                >
                    {emoji}
                </button>
            ))}
        </div>
    );

    const { connected, connecting, error } = socketStatus ?? {};
    const socketLabel = connected ? 'Live' : error ? `Offline: ${error}` : connecting ? 'Connecting…' : 'Offline';
    const socketColor = connected ? '#22c55e' : error ? '#ef4444' : '#eab308';

    return (
        <Container $display="flex" $flexDirection="column" $gap="10px">
            {attachmentPreview?.kind === 'image' ? (
                composeImagePreviewUrls.length > 0 ? (
                    <AntImagePreview
                        open
                        onClose={closeImagePreviewAndRevokeBlob}
                        zIndex={modalZIndex}
                        {...(composeImagePreviewUrls.length > 1
                            ? {
                                sources: composeImagePreviewUrls,
                                startIndex: composeImageStartIndex,
                                onSlideChange: handleComposeImageSlide,
                            }
                            : { src: composeImagePreviewUrls[0] })}
                    />
                ) : attachmentPreview.src ? (
                    <AntImagePreview
                        open
                        onClose={closeImagePreviewAndRevokeBlob}
                        src={attachmentPreview.src}
                        zIndex={modalZIndex}
                    />
                ) : null
            ) : null}

            <Modal
                open={!!attachmentPreview && attachmentPreview.kind === 'video'}
                onCancel={closeAttachmentPreview}
                afterClose={() => {
                    const s = lastPreviewSrcRef.current;
                    if (s) URL.revokeObjectURL(s);
                    lastPreviewSrcRef.current = null;
                }}
                footer={null}
                centered
                width="min(92vw, 980px)"
                motion={{ motionName: 'zoom', maskMotionName: 'fade' }}
                closeIcon={<CloseOutlined style={{ color: 'rgba(255,255,255,0.92)', fontSize: 18 }} />}
                styles={{
                    content: {
                        padding: 0,
                        overflow: 'hidden',
                        borderRadius: 14,
                        background: '#0b0f19',
                        border: '1px solid rgba(255,255,255,0.10)',
                    },
                    header: {
                        background: '#0b0f19',
                        borderBottom: '1px solid rgba(255,255,255,0.10)',
                        marginBottom: 0,
                        padding: '12px 14px',
                    },
                    body: {
                        padding: 0,
                        background: '#0b0f19',
                    },
                    mask: { background: 'rgba(2,6,23,0.68)' },
                }}
                title={
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <div style={{ fontWeight: 900, fontSize: 14, color: 'rgba(255,255,255,0.92)' }}>
                            {attachmentPreview?.name || 'Video'}
                        </div>
                        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>Preview before sending</div>
                    </div>
                }
                zIndex={modalZIndex}
            >
                <div
                    style={{
                        position: 'relative',
                        height: 'min(70vh, 680px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 18,
                    }}
                >
                    {attachmentPreview?.galleryIds?.length > 1 ? (
                        <>
                            <button
                                type="button"
                                aria-label="Previous attachment"
                                onClick={() => navigateAttachmentGallery(-1)}
                                style={{
                                    position: 'absolute',
                                    left: 10,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    zIndex: 2,
                                    width: 40,
                                    height: 40,
                                    borderRadius: 999,
                                    border: '1px solid rgba(255,255,255,0.18)',
                                    background: 'rgba(15,23,42,0.65)',
                                    color: 'rgba(255,255,255,0.92)',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <LeftOutlined />
                            </button>
                            <button
                                type="button"
                                aria-label="Next attachment"
                                onClick={() => navigateAttachmentGallery(1)}
                                style={{
                                    position: 'absolute',
                                    right: 10,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    zIndex: 2,
                                    width: 40,
                                    height: 40,
                                    borderRadius: 999,
                                    border: '1px solid rgba(255,255,255,0.18)',
                                    background: 'rgba(15,23,42,0.65)',
                                    color: 'rgba(255,255,255,0.92)',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <RightOutlined />
                            </button>
                        </>
                    ) : null}
                    {attachmentPreview?.src ? (
                        <video
                            key={attachmentPreview.src}
                            src={attachmentPreview.src}
                            controls
                            playsInline
                            style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                            }}
                        />
                    ) : null}
                </div>
            </Modal>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 4,
                    width: '100%',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
                    {toolbarBtn('bold', BoldOutlined, 'Bold', 'bold', formatState.bold)}
                    {toolbarBtn('italic', ItalicOutlined, 'Italic', 'italic', formatState.italic)}
                    {toolbarBtn('underline', UnderlineOutlined, 'Underline', 'underline', formatState.underline)}
                    {toolbarBtn('strikethrough', StrikethroughOutlined, 'Strikethrough', 'strikethrough', formatState.strikethrough)}
                    <span
                        style={{
                            width: 1,
                            height: 18,
                            background: dark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)',
                            margin: '0 4px',
                            flexShrink: 0,
                        }}
                        aria-hidden
                    />
                    <Tooltip title={<TooltipTitleWithKeys label="Bullet list" keys={getKeysForOs('unorderedList')} />} {...tooltipProps}>
                        <button
                            type="button"
                            aria-label="Bullet list"
                            style={toolBtnBase(formatState.insertUnorderedList)}
                            onMouseEnter={(e) => {
                                if (!formatState.insertUnorderedList) hoverIn(e);
                            }}
                            onMouseLeave={(e) => {
                                if (!formatState.insertUnorderedList) hoverOut(e);
                            }}
                            onClick={() => applyFormat('insertUnorderedList')}
                        >
                            <UnorderedListOutlined />
                        </button>
                    </Tooltip>
                    <Tooltip title={<TooltipTitleWithKeys label="Numbered list" keys={getKeysForOs('orderedList')} />} {...tooltipProps}>
                        <button
                            type="button"
                            aria-label="Numbered list"
                            style={toolBtnBase(formatState.insertOrderedList)}
                            onMouseEnter={(e) => {
                                if (!formatState.insertOrderedList) hoverIn(e);
                            }}
                            onMouseLeave={(e) => {
                                if (!formatState.insertOrderedList) hoverOut(e);
                            }}
                            onClick={() => applyFormat('insertOrderedList')}
                        >
                            <OrderedListOutlined />
                        </button>
                    </Tooltip>
                    <Tooltip title={<TooltipTitleWithKeys label="Code block" keys={getKeysForOs('codeBlock')} />} {...tooltipProps}>
                        <button
                            type="button"
                            aria-label="Code block"
                            style={toolBtnBase(formatState.inPre)}
                            onMouseEnter={(e) => {
                                if (!formatState.inPre) hoverIn(e);
                            }}
                            onMouseLeave={(e) => {
                                if (!formatState.inPre) hoverOut(e);
                            }}
                            onClick={togglePre}
                        >
                            <ComposeCodeBlockIcon />
                        </button>
                    </Tooltip>
                    <Tooltip title={<TooltipTitleWithKeys label="Code" keys={getKeysForOs('inlineCode')} />} {...tooltipProps}>
                        <button
                            type="button"
                            aria-label="Code"
                            disabled={formatState.inPre}
                            style={{
                                ...toolBtnBase(formatState.inInlineCode),
                                ...(formatState.inPre ? { opacity: 0.35, cursor: 'not-allowed' } : {}),
                            }}
                            onMouseEnter={(e) => {
                                if (formatState.inPre) return;
                                if (!formatState.inInlineCode) hoverIn(e);
                            }}
                            onMouseLeave={(e) => {
                                if (formatState.inPre) return;
                                if (!formatState.inInlineCode) hoverOut(e);
                            }}
                            onClick={toggleInlineCode}
                        >
                            <ComposeInlineCodeIcon />
                        </button>
                    </Tooltip>
                    <Tooltip title={<TooltipTitleWithKeys label="Blockquote" keys={getKeysForOs('blockquote')} />} {...tooltipProps}>
                        <button
                            type="button"
                            aria-label="Blockquote"
                            style={toolBtnBase(formatState.inBlockquote)}
                            onMouseEnter={(e) => {
                                if (!formatState.inBlockquote) hoverIn(e);
                            }}
                            onMouseLeave={(e) => {
                                if (!formatState.inBlockquote) hoverOut(e);
                            }}
                            onClick={toggleBlockquote}
                        >
                            <CommentOutlined />
                        </button>
                    </Tooltip>
                    <span
                        style={{
                            width: 1,
                            height: 18,
                            background: dark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)',
                            margin: '0 4px',
                            flexShrink: 0,
                        }}
                        aria-hidden
                    />
                    <Tooltip title={<TooltipTitleWithKeys label="Add link" keys={getKeysForOs('link')} />} {...tooltipProps}>
                        <button
                            type="button"
                            aria-label="Add link"
                            style={toolBtnBase(pendingLinkAttachments.length > 0)}
                            onMouseEnter={(e) => {
                                if (!pendingLinkAttachments.length) hoverIn(e);
                            }}
                            onMouseLeave={(e) => {
                                if (!pendingLinkAttachments.length) hoverOut(e);
                            }}
                            onClick={openLinkModal}
                        >
                            <LinkOutlined />
                        </button>
                    </Tooltip>
                    <Tooltip title="Attach file" {...tooltipProps}>
                        <button
                            type="button"
                            aria-label="Attach file"
                            style={toolBtnBase(false)}
                            onMouseEnter={hoverIn}
                            onMouseLeave={hoverOut}
                            onClick={() => documentInputRef.current?.click()}
                        >
                            <PaperClipOutlined />
                        </button>
                    </Tooltip>
                    {socketStatus != null && (
                        <span
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                fontSize: 11,
                                color: dark ? 'rgba(255,255,255,0.5)' : '#666',
                                flexShrink: 0,
                                marginLeft: 4,
                            }}
                            title={error ?? (connected ? 'Live' : connecting ? 'Connecting…' : 'Offline')}
                        >
                            <span
                                style={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    background: socketColor,
                                }}
                            />
                            {socketLabel}
                        </span>
                    )}
                </div>
            </div>

            <ComposeEditorShell
                ref={composeShellRef}
                $dark={dark}
                onDragEnter={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                }}
                onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleComposerDrop}
                style={{
                    width: '100%',
                    position: 'relative',
                    boxShadow: dragOver
                        ? `inset 0 0 0 2px ${dark ? 'rgba(139,92,246,0.75)' : 'rgba(139,92,246,0.55)'}`
                        : 'none',
                    borderRadius: 12,
                }}
            >
                <div
                    ref={editorRef}
                    className="compose-editor-root"
                    contentEditable={!disabled}
                    data-placeholder={placeholder}
                    role="textbox"
                    aria-multiline="true"
                    style={{
                        width: '100%',
                        ...(isEmpty ? { position: 'relative' } : {}),
                    }}
                    onInput={handleEditorInput}
                    onPaste={handleEditorPaste}
                    onKeyDown={handleKeyDownEditor}
                    onKeyUp={() => {
                        // Some browsers can miss an input event for certain IME paths; keep suggest responsive.
                        tryOpenSuggestFromCaret();
                    }}
                    onBlur={() => {
                        if (suggestOpen) closeSuggest();
                    }}
                    onMouseUp={handleEditorMouseUp}
                    onClick={handleComposerClick}
                    suppressContentEditableWarning
                />
                {isEmpty && (
                    <span
                        style={{
                            position: 'absolute',
                            left: 12,
                            top: 6,
                            pointerEvents: 'none',
                            color: dark ? 'rgba(255,255,255,0.35)' : '#bfbfbf',
                            fontSize: 14,
                            lineHeight: 1.4,
                        }}
                    >
                        {placeholder}
                    </span>
                )}
                {suggestOpen && suggestPos && typeof document !== 'undefined' &&
                    createPortal((
                        <div
                            ref={suggestDropdownRef}
                            style={{
                                position: 'fixed',
                                top: suggestPos.top ?? 0,
                                left: suggestPos.left ?? 0,
                                minWidth: 280,
                                maxWidth: 360,
                                maxHeight: 270,
                                overflowX: 'hidden',
                                overflowY: 'auto',
                                boxSizing: 'border-box',
                                background: dark ? (theme?.colors?.background?.secondary || '#1f1f1f') : '#fff',
                                border: `1px solid ${dark ? 'rgba(255,255,255,0.14)' : '#d9d9d9'}`,
                                borderRadius: 12,
                                boxShadow: dark
                                    ? '0 18px 50px rgba(0,0,0,0.55)'
                                    : '0 18px 50px rgba(15,23,42,0.18)',
                                zIndex: 20000,
                                padding: '6px 8px',
                                backdropFilter: 'blur(10px)',
                            }}
                            onMouseDown={(e) => e.preventDefault()}
                        >
                            {filteredSuggestItems.length === 0 ? (
                                <div style={{ padding: '10px 12px', fontSize: 12, opacity: 0.8 }}>
                                    {suggestType === 'mention'
                                        ? 'No members found'
                                        : suggestType === 'tag'
                                            ? 'No tags found'
                                            : 'No suggestions'}
                                </div>
                            ) : (
                                (() => {
                                    const sectionOrder =
                                        suggestType === 'mention' ? ['user', 'milestone', 'sprint'] : ['tag'];
                                    const sectionLabel = (t) =>
                                        t === 'user' ? 'Users' : t === 'milestone' ? 'Milestones' : t === 'sprint' ? 'Sprints' : 'Tags';

                                    const highlightIdx = suggestHighlightIndexRef.current ?? 0;
                                    const itemKey = (it, idx) => `${it.type || suggestType}-${it.id ?? idx}`;

                                    const rowStyle = (active) => ({
                                        display: 'block',
                                        width: '100%',
                                        maxWidth: '100%',
                                        boxSizing: 'border-box',
                                        textAlign: 'left',
                                        border: 'none',
                                        background: active
                                            ? primary
                                            : dark
                                                ? 'rgba(255,255,255,0.02)'
                                                : 'rgba(2,6,23,0.02)',
                                        color: active ? '#fff' : dark ? 'rgba(255,255,255,0.9)' : '#0f172a',
                                        padding: '9px 12px',
                                        fontSize: 14,
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                        transition: 'background 0.12s ease, color 0.12s ease, transform 0.12s ease',
                                        borderRadius: 8,
                                        margin: '2px 0',
                                    });

                                    const headerStyle = (first) => ({
                                        padding: '10px 12px 6px',
                                        marginTop: first ? 0 : 6,
                                        borderBottom: `1px solid ${dark ? 'rgba(255,255,255,0.12)' : '#e5e7eb'}`,
                                        marginBottom: 2,
                                        fontSize: 11,
                                        fontWeight: 700,
                                        letterSpacing: '0.06em',
                                        textTransform: 'uppercase',
                                        color: dark ? 'rgba(255,255,255,0.5)' : '#71717a',
                                        lineHeight: 1.25,
                                        userSelect: 'none',
                                    });

                                    let firstHeader = true;
                                    return sectionOrder.map((sectionType) => {
                                        const sectionItems = filteredSuggestItems.filter((it) =>
                                            suggestType === 'mention' ? it.type === sectionType : true,
                                        );
                                        if (sectionItems.length === 0) return null;
                                        const firstIndex = filteredSuggestItems.indexOf(sectionItems[0]);
                                        const header = (
                                            <div key={`${sectionType}-header`} style={headerStyle(firstHeader)}>
                                                {sectionLabel(sectionType)}
                                            </div>
                                        );
                                        firstHeader = false;
                                        return (
                                            <div key={sectionType} style={{ minWidth: 0, maxWidth: '100%' }}>
                                                {header}
                                                {sectionItems.map((it, idx) => {
                                                    const flatIndex = firstIndex + idx;
                                                    const active = flatIndex === highlightIdx;
                                                    const title =
                                                        suggestType === 'mention'
                                                            ? it.type === 'user'
                                                                ? `@${it.label || it.email || ''}`
                                                                : String(it.label || '')
                                                            : String(it.tag || '');
                                                    return (
                                                        <button
                                                            key={itemKey(it, flatIndex)}
                                                            type="button"
                                                            onMouseEnter={() => {
                                                                suggestHighlightIndexRef.current = flatIndex;
                                                                setSuggestQuery((q) => q);
                                                            }}
                                                            onClick={() => insertSuggestItem(it)}
                                                            style={rowStyle(active)}
                                                        >
                                                            <div
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 10,
                                                                    minWidth: 0,
                                                                    maxWidth: '100%',
                                                                    overflow: 'hidden',
                                                                }}
                                                            >
                                                                {suggestType === 'mention' && it.type === 'user' ? (
                                                                    <span
                                                                        aria-hidden
                                                                        style={{
                                                                            width: 28,
                                                                            height: 28,
                                                                            borderRadius: '50%',
                                                                            flexShrink: 0,
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            fontSize: 12,
                                                                            fontWeight: 800,
                                                                            background: active
                                                                                ? 'rgba(255,255,255,0.20)'
                                                                                : dark
                                                                                    ? 'rgba(255,255,255,0.08)'
                                                                                    : 'rgba(2,6,23,0.08)',
                                                                            color: active ? '#fff' : dark ? 'rgba(255,255,255,0.85)' : '#111827',
                                                                        }}
                                                                    >
                                                                        {String(it.label || it.email || '?')
                                                                            .trim()
                                                                            .split(/\s+/)
                                                                            .slice(0, 2)
                                                                            .map((s) => s[0])
                                                                            .join('')
                                                                            .toUpperCase()}
                                                                    </span>
                                                                ) : (
                                                                    <span
                                                                        aria-hidden
                                                                        style={{
                                                                            width: 8,
                                                                            height: 8,
                                                                            borderRadius: 99,
                                                                            flexShrink: 0,
                                                                            background: active ? 'rgba(255,255,255,0.70)' : dark ? 'rgba(255,255,255,0.25)' : 'rgba(2,6,23,0.25)',
                                                                        }}
                                                                    />
                                                                )}
                                                                <div style={{ minWidth: 0, flex: 1 }}>
                                                                    <div
                                                                        style={{
                                                                            whiteSpace: 'nowrap',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                        }}
                                                                    >
                                                                        {title}
                                                                    </div>
                                                                    {suggestType === 'mention' && it.type === 'user' && it.email ? (
                                                                        <div
                                                                            style={{
                                                                                fontSize: 11,
                                                                                opacity: active ? 0.9 : 0.65,
                                                                                whiteSpace: 'nowrap',
                                                                                overflow: 'hidden',
                                                                                textOverflow: 'ellipsis',
                                                                            }}
                                                                        >
                                                                            {it.email}
                                                                        </div>
                                                                    ) : null}
                                                                </div>
                                                            </div>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        );
                                    });
                                })()
                            )}
                        </div>
                    ), document.body)
                }
            </ComposeEditorShell>

            {pendingLinkAttachments.length > 0 && (
                <Container $display="flex" $flexDirection="column" $gap="8px" style={{ width: '100%' }}>
                    {pendingLinkAttachments.map((link) => (
                        <div
                            key={link.id}
                            style={{
                                alignSelf: 'stretch',
                                maxWidth: '100%',
                                padding: '10px 12px',
                                borderRadius: 8,
                                border: `1px solid ${dark ? 'rgba(255,255,255,0.12)' : '#e5e7eb'}`,
                                background: dark ? 'rgba(255,255,255,0.04)' : '#f8fafc',
                                textAlign: 'left',
                            }}
                        >
                            <div
                                style={{
                                    fontSize: 11,
                                    color: dark ? 'rgba(255,255,255,0.45)' : '#64748b',
                                    marginBottom: 6,
                                    fontWeight: 500,
                                }}
                            >
                                {new Date(link.addedAt ?? Date.now()).toLocaleString(undefined, {
                                    month: 'short',
                                    day: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit',
                                })}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                <a
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        fontSize: 14,
                                        fontWeight: 600,
                                        color: dark ? '#93c5fd' : '#2563eb',
                                        textDecoration: 'none',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        flex: 1,
                                        minWidth: 0,
                                    }}
                                >
                                    {link.title}
                                </a>
                                <button
                                    type="button"
                                    aria-label="Remove link"
                                    onClick={() => removePendingLink(link.id)}
                                    style={{
                                        border: 'none',
                                        background: 'transparent',
                                        color: dark ? 'rgba(255,255,255,0.45)' : '#94a3b8',
                                        cursor: 'pointer',
                                        padding: 4,
                                        lineHeight: 1,
                                    }}
                                >
                                    <CloseOutlined />
                                </button>
                            </div>
                        </div>
                    ))}
                </Container>
            )}

            {recordingError && (
                <span style={{ fontSize: 12, color: '#ef4444', paddingLeft: 4 }}>{recordingError}</span>
            )}

            {attachedFiles.length > 0 && (
                <Container $display="flex" $gap="8px" $flexWrap="wrap" $alignItems="flex-start">
                    {attachedFiles.map((item) => (
                        <AttachmentThumb
                            key={item.id}
                            item={item}
                            dark={dark}
                            onRemove={() => removeAttachment(item.id)}
                            onPreview={openAttachmentPreview}
                        />
                    ))}
                </Container>
            )}

            <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => {
                    const files = e.target.files;
                    if (files?.length) {
                        addAttachments(files);
                        focusComposerAfterFilePick();
                    }
                    onAttachImage?.(e);
                    e.target.value = '';
                }}
            />
            <input
                type="file"
                ref={videoInputRef}
                accept="video/*"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => {
                    const files = e.target.files;
                    if (files?.length) {
                        addAttachments(files);
                        focusComposerAfterFilePick();
                    }
                    e.target.value = '';
                }}
            />
            <input
                type="file"
                ref={audioInputRef}
                accept="audio/*"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => {
                    const files = e.target.files;
                    if (files?.length) {
                        addAttachments(files);
                        focusComposerAfterFilePick();
                    }
                    e.target.value = '';
                }}
            />
            <input
                type="file"
                ref={documentInputRef}
                multiple
                style={{ display: 'none' }}
                onChange={(e) => {
                    const files = e.target.files;
                    if (files?.length) {
                        addAttachments(files);
                        focusComposerAfterFilePick();
                    }
                    onAttachDocument?.(e);
                    e.target.value = '';
                }}
            />

            <Modal
                maskClosable={false}
                title="Take a photo"
                open={cameraOpen}
                onCancel={closeCameraModal}
                destroyOnHidden
                zIndex={modalZIndex}
                footer={[
                    <button
                        key="cancel"
                        type="button"
                        onClick={closeCameraModal}
                        style={{
                            padding: '6px 16px',
                            borderRadius: 8,
                            border: '1px solid #d9d9d9',
                            background: '#fff',
                            cursor: 'pointer',
                        }}
                    >
                        Cancel
                    </button>,
                    <button
                        key="capture"
                        type="button"
                        onClick={capturePhoto}
                        disabled={!!cameraError || cameraLoading}
                        style={{
                            padding: '6px 16px',
                            borderRadius: 8,
                            border: 'none',
                            background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                            color: '#fff',
                            cursor: cameraError || cameraLoading ? 'not-allowed' : 'pointer',
                            opacity: cameraError || cameraLoading ? 0.6 : 1,
                        }}
                    >
                        Capture
                    </button>,
                ]}
            >
                {cameraLoading && <p style={{ textAlign: 'center', padding: 24 }}>Requesting camera access…</p>}
                {cameraError && <p style={{ color: '#ff4d4f', padding: 16 }}>{cameraError}</p>}
                {!cameraError && !cameraLoading && (
                    <div style={{ textAlign: 'center' }}>
                        <video
                            ref={setVideoRef}
                            autoPlay
                            playsInline
                            muted
                            style={{
                                width: '100%',
                                maxHeight: 360,
                                background: '#000',
                                borderRadius: 8,
                                objectFit: 'contain',
                            }}
                        />
                        <canvas ref={canvasRef} style={{ display: 'none' }} />
                    </div>
                )}
            </Modal>

            <Modal
                maskClosable={false}
                title="Record video"
                open={videoRecordOpen}
                onCancel={closeVideoRecordModal}
                zIndex={modalZIndex}
                footer={[
                    <button
                        key="cancel"
                        type="button"
                        onClick={closeVideoRecordModal}
                        style={{
                            padding: '6px 16px',
                            borderRadius: 8,
                            border: '1px solid #d9d9d9',
                            background: '#fff',
                            cursor: 'pointer',
                        }}
                    >
                        Cancel
                    </button>,
                    isRecordingVideo ? (
                        <button
                            key="stop"
                            type="button"
                            onClick={stopVideoRecording}
                            style={{
                                padding: '6px 16px',
                                borderRadius: 8,
                                border: 'none',
                                background: '#ef4444',
                                color: '#fff',
                                cursor: 'pointer',
                            }}
                        >
                            Stop & attach
                        </button>
                    ) : (
                        <button
                            key="record"
                            type="button"
                            onClick={startVideoRecording}
                            disabled={!!videoRecordError || videoRecordLoading}
                            style={{
                                padding: '6px 16px',
                                borderRadius: 8,
                                border: 'none',
                                background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                                color: '#fff',
                                cursor: videoRecordError || videoRecordLoading ? 'not-allowed' : 'pointer',
                                opacity: videoRecordError || videoRecordLoading ? 0.6 : 1,
                            }}
                        >
                            Start recording
                        </button>
                    ),
                ]}
            >
                {videoRecordLoading && <p style={{ textAlign: 'center', padding: 24 }}>Requesting camera and microphone…</p>}
                {videoRecordError && <p style={{ color: '#ff4d4f', padding: 16 }}>{videoRecordError}</p>}
                {!videoRecordError && !videoRecordLoading && (
                    <div style={{ textAlign: 'center' }}>
                        <video
                            ref={videoRecordPreviewRef}
                            autoPlay
                            playsInline
                            muted
                            style={{
                                width: '100%',
                                maxHeight: 360,
                                background: '#000',
                                borderRadius: 8,
                                objectFit: 'contain',
                            }}
                        />
                        {isRecordingVideo && (
                            <p style={{ marginTop: 8, color: '#ef4444', fontSize: 14 }}>Recording… Click "Stop & attach" when done.</p>
                        )}
                    </div>
                )}
            </Modal>

            <AppModal
                size="sm"
                title="Add link"
                open={linkModalOpen}
                onCancel={() => {
                    setLinkModalOpen(false);
                    linkForm.resetFields();
                }}
                footer={null}
                zIndex={modalZIndex}
            >
                <CreateRoleModalBody>
                    <Form form={linkForm} layout="vertical">
                        <Form.Item name="url" label="URL" rules={[{ required: true, message: 'Enter URL' }]}>
                            <Input placeholder="https://..." />
                        </Form.Item>
                        <Form.Item name="title" label="Link text">
                            <Input placeholder="Optional display text" />
                        </Form.Item>
                    </Form>
                </CreateRoleModalBody>
                <CreateRoleModalFooter>
                    <Button
                        type="button"
                        $primary={false}
                        $size="14px"
                        onClick={() => {
                            setLinkModalOpen(false);
                            linkForm.resetFields();
                        }}
                    >
                        Cancel
                    </Button>
                    <Button type="button" $primary $blackOnLight $size="14px" onClick={handleLinkModalSubmit}>
                        Add link
                    </Button>
                </CreateRoleModalFooter>
            </AppModal>

            <Container $display="flex" $alignItems="center" $gap="4px" $flexWrap="wrap" style={{ width: '100%' }}>
                <Dropdown
                    open={attachMenuOpen}
                    onOpenChange={setAttachMenuOpen}
                    popupRender={() => attachMenuOverlay}
                    trigger={['click']}
                    placement="top"
                    overlayStyle={typeof popupZIndex === 'number' ? { zIndex: popupZIndex } : undefined}
                >
                    <Tooltip title="Attach" {...tooltipProps}>
                        <button
                            type="button"
                            aria-label="Attach"
                            style={attachmentBtnStyle}
                            onMouseEnter={hoverIn}
                            onMouseLeave={hoverOut}
                        >
                            <PlusOutlined />
                        </button>
                    </Tooltip>
                </Dropdown>
                <Dropdown
                    open={emojiOpen}
                    onOpenChange={setEmojiOpen}
                    popupRender={() => emojiDropdown}
                    trigger={['click']}
                    placement="top"
                    overlayStyle={typeof popupZIndex === 'number' ? { zIndex: popupZIndex } : undefined}
                >
                    <Tooltip title="Emoji" {...tooltipProps}>
                        <button
                            type="button"
                            aria-label="Emoji"
                            style={attachmentBtnStyle}
                            onMouseEnter={hoverIn}
                            onMouseLeave={hoverOut}
                        >
                            <SmileOutlined />
                        </button>
                    </Tooltip>
                </Dropdown>
                <span
                    aria-hidden
                    style={{
                        width: 1,
                        height: 18,
                        background: dark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)',
                        margin: '0 6px',
                        flexShrink: 0,
                    }}
                />
                <Tooltip title="Record video" {...tooltipProps}>
                    <button
                        type="button"
                        aria-label="Record video"
                        style={attachmentBtnStyle}
                        onMouseEnter={hoverIn}
                        onMouseLeave={hoverOut}
                        onClick={openVideoRecordModal}
                    >
                        <VideoCameraOutlined />
                    </button>
                </Tooltip>
                <Tooltip title="Record voice message" {...tooltipProps}>
                    <button
                        type="button"
                        aria-label="Record voice"
                        style={attachmentBtnStyle}
                        onMouseEnter={hoverIn}
                        onMouseLeave={hoverOut}
                        onClick={handleMicClick}
                    >
                        <AudioOutlined />
                    </button>
                </Tooltip>
                <Tooltip title="Camera" {...tooltipProps}>
                    <button
                        type="button"
                        aria-label="Camera"
                        style={attachmentBtnStyle}
                        onMouseEnter={hoverIn}
                        onMouseLeave={hoverOut}
                        onClick={openCameraModal}
                    >
                        <CameraOutlined />
                    </button>
                </Tooltip>
                {isRecording ? (
                    <span
                        style={{
                            marginLeft: 'auto',
                            flexShrink: 0,
                            lineHeight: 0,
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <Tooltip title="Stop recording and send" {...tooltipProps}>
                            <button
                                type="button"
                                aria-label="Stop recording and send"
                                onClick={stopRecording}
                                style={{
                                    border: 'none',
                                    background: 'transparent',
                                    cursor: 'pointer',
                                    padding: '6px 10px',
                                    borderRadius: 8,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    lineHeight: 0,
                                }}
                            >
                                <RecordingWaveBars color={primary} />
                            </button>
                        </Tooltip>
                    </span>
                ) : disabled ? null : canSend ? (
                    <span style={{ marginLeft: 'auto', flexShrink: 0, lineHeight: 0 }}>
                        <button
                            type="button"
                            aria-label="Send"
                            onClick={handleSendClick}
                            style={sendBtnGradientStyle}
                        >
                            <SendOutlined style={{ fontSize: 16 }} />
                        </button>
                    </span>
                ) : (
                    <span style={{ marginLeft: 'auto', flexShrink: 0, lineHeight: 0 }}>
                        <Button
                            type="button"
                            aria-label="Send"
                            icon={<SendOutlined style={{ fontSize: 16 }} />}
                            disabled
                            lockWhenDisabled
                            disabledTooltip="Type a message or add an attachment to send."
                            onClick={handleSendClick}
                            $primary
                            $blackOnLight
                            $size="14px"
                            $padding="8px 12px"
                            $hoverShadow={false}
                            style={{ minWidth: 40, height: 40, borderRadius: 8 }}
                        />
                    </span>
                )}
            </Container>
        </Container>
    );
});
